/* ============================================================
   CALCULATION ENGINE v6 — pure functions, ESM, no DOM
   Input data: BALANCES, SALARY, RECURRING, FAMILY_AP, AR_RECURRING,
               INCOMES, MORTGAGES, CC_MIN_PAYMENTS, PRESETS
   v6 additions:
     • piecewiseAmountInMonth(schedule, ym) — sums all entries in a YM
     • calcLineForMonth(line, ym, defaultMonthly) — piecewise OR default
     • jpMode === 'custom' path uses scenario.jpAmount
     • RECURRING.monthly / yearly / quarterly all honor paymentSchedule
     • FAMILY_AP.direct + FAMILY_AP.viaMarian honor paymentSchedule
     • Cross-reference REGISTRY — metric → consumer sites
   v4 retained:
     • FAMILY_AP = { direct, viaMarian }
     • Sibling toggles: scenario.jpMode, jcAmount, jmAmount
  • Financing piecewise repayment via scenario.jmLoanRepaySchedule
     • Mortgages use imported bill snapshot amounts
   ============================================================ */

import {
  BALANCES, SALARY, RECURRING, FAMILY_AP, AR_RECURRING,
  INCOMES, MORTGAGES, CC_MIN_PAYMENTS,
  SEASONAL, LIFESTYLE, INFLATION, FUEL, FVF_DRIVERS,
  SNAPSHOT_AS_OF,
  CHART_OF_ACCOUNTS,
  /* v7.2 data blocks — balance-sheet sources of truth (v7.3.2 formal BS) */
  FIXED_ASSETS, LONG_TERM_LIABILITIES, CASH_BALANCES, CC_LIABILITIES,
  /* v8 Path N — used by buildSimulationsRegistry to diff scenario vs base preset */
  PRESETS,
} from './data.js';
import {
  formatDateOnly,
  formatMoney,
  formatMoneyShort,
  formatPlainNumber,
} from './formatting.js';

/* ============================================================
   v7.1 — ACCOUNT VISIBILITY
   ============================================================ */

const TIER_ORDER = { primary: 0, secondary: 1, dust: 2 };

/* Per-account visibility resolution.
   Resolution order:
   1. Explicit per-account override in scenario.accountVisibility[id] (true/false).
   2. Optional per-tab tier filter: scenario.perTabTierFilter[tabId] (v7.3).
   3. Global scenario.accountTierFilter ('primary' | 'primarySecondary' | 'all').

   The `tabId` parameter is optional — existing callers that omit it get the
   global filter for full backward compatibility.
*/
export function resolveAccountVisibility(account, scenario, tabId) {
  const override = scenario?.accountVisibility?.[account.id];
  if (typeof override === 'boolean') return override;

  const perTab = tabId ? scenario?.perTabTierFilter?.[tabId] : undefined;
  const filter = perTab ?? scenario?.accountTierFilter ?? 'primarySecondary';

  const tierLevel = TIER_ORDER[account.tier] ?? 0;
  if (filter === 'primary')          return tierLevel <= 0;
  if (filter === 'primarySecondary') return tierLevel <= 1;
  return true; /* 'all' */
}

/* Filter helper used by tabs */
export function visibleAccounts(scenario, predicate) {
  return CHART_OF_ACCOUNTS.filter((a) =>
    resolveAccountVisibility(a, scenario) && (predicate ? predicate(a) : true)
  );
}

/* Counts by group — for the Settings panel header chips */
export function visibilityCounts(scenario) {
  const counts = {};
  for (const a of CHART_OF_ACCOUNTS) {
    counts[a.type] = counts[a.type] || { visible: 0, total: 0 };
    counts[a.type].total++;
    if (resolveAccountVisibility(a, scenario)) counts[a.type].visible++;
  }
  return counts;
}

/* ============================================================
   v7.1 — ACCOUNT- + TRANSACTION-LEVEL DRILL-DOWN
   ============================================================ */

/* Per-category rollup from CHART_OF_ACCOUNTS. Optional `categoryKey`:
   - 'Software' / 'Health' / 'Family' / 'Bank Fees' / 'Membership' / etc.  → matches account.cat
   - 'recurring.monthly' / 'recurring.yearly' / 'recurring.quarterly'      → matches subtype prefix
   - 'mortgage' / 'family' / 'cash' / 'bank' / 'creditCard' / 'longTerm'   → matches subtype directly
   Returns visible accounts only (honors scenario.accountVisibility).
*/
export function accountBreakdown(categoryKey, scenario, ym) {
  if (!categoryKey) return [];
  const rows = CHART_OF_ACCOUNTS
    .filter((a) => {
      if (!resolveAccountVisibility(a, scenario)) return false;
      /* Match by .cat (recurring categories) or by .subtype */
      if (a.cat && a.cat === categoryKey) return true;
      if (a.subtype === categoryKey) return true;
      if (categoryKey.startsWith('recurring.')) {
        const wanted = categoryKey.slice('recurring.'.length);
        return a.subtype === 'recurring' && a.id.includes(`recurring.${wanted}.`);
      }
      return false;
    })
    .map((a) => ({
      accountId: a.id,
      label: a.label,
      cat: a.cat,
      subtype: a.subtype,
      monthly: a.monthly ?? null,
      balance: a.balance ?? null,
      source: a.source,
      tier: a.tier,
      fx: !!a.fx,
    }));

  return rows.sort((a, b) => {
    const av = Math.abs(a.monthly ?? a.balance ?? 0);
    const bv = Math.abs(b.monthly ?? b.balance ?? 0);
    return bv - av;
  });
}

/* Category-level rollup of recurring spend. Each row gets a `categoryKey`
   field so the ExplainModal can drill into per-account view. */
export function recurringCategoryBreakdown(scenario, ym) {
  const cats = {};
  for (const r of CHART_OF_ACCOUNTS) {
    if (r.subtype !== 'recurring' || !resolveAccountVisibility(r, scenario)) continue;
    const cat = r.cat || 'Uncategorized';
    cats[cat] = (cats[cat] || 0) + (r.monthly || 0);
  }
  return Object.entries(cats)
    .map(([cat, amount]) => ({
      label: cat,
      amount,
      source: 'CHART_OF_ACCOUNTS',
      cat,
      categoryKey: cat,                     /* ← triggers ExplainModal account expander */
    }))
    .sort((a, b) => b.amount - a.amount);
}

/* v7.2 — aggregate posted adjusting entries by account + statement.
   Returns { byAccount: Map<accountName, net>, totals: { is, bs } }.
   IS net = credits − debits for income/revenue; debits − credits for expense.
   For simplicity we just return the signed amount per account
   (positive credit, negative debit) — callers interpret based on sign. */
export function applyAdjustingEntries(scenario) {
  const entries = (scenario?.adjustingEntries ?? []).filter(e => e.status === 'posted');
  const byAccount = {};
  let is = 0;
  let bs = 0;
  for (const e of entries) {
    for (const l of (e.lines ?? [])) {
      if (!l.account) continue;
      const signed = (Number(l.credit) || 0) - (Number(l.debit) || 0);
      byAccount[l.account] = (byAccount[l.account] || 0) + signed;
      const affects = e.affects ?? [];
      if (affects.includes('is')) is += signed;
      if (affects.includes('bs')) bs += signed;
    }
  }
  return {
    byAccount,
    totals: { is, bs },
    count: entries.length,
    entries,
  };
}

/* Transaction drill-in — infrastructure kept, data source pending.

   Phase 1.5 removed the provider-specific mirror that used to populate this cache.
   The cache shape + API surface are preserved so ExplainModal keeps working;
   a future phase will repopulate it from the atomic-ledger-contract (either
   a new /api/transactions/:accountId endpoint, or by indexing journalEntries
   client-side at load time).

   Until then, transactionBreakdown returns an empty/CTA state so the UI
   degrades gracefully. No import reaches outside this package. */
let _txCacheRef = null;

export function setTransactionCache(cache) {
  _txCacheRef = cache;
}

function normalizeAcctName(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/x{2,}/g, 'x');   // collapse xx/xxxx → x
}

export function transactionBreakdown(accountId, _scenario, _ym, accountName = null) {
  if (!accountId && !accountName) {
    return { transactions: [], hasSync: false, ctaMessage: 'No account selected' };
  }
  if (_txCacheRef) {
    /* Lookup: 1) direct id 2) direct name 3) normalized name fallback. */
    let txns = _txCacheRef.byAccountId?.[accountId];
    if (!txns && accountName) txns = _txCacheRef.byAccountName?.[accountName];
    if (!txns && accountId)   txns = _txCacheRef.byAccountName?.[accountId];
    if (!txns) {
      const target = normalizeAcctName(accountName || accountId);
      for (const [name, arr] of Object.entries(_txCacheRef.byAccountName || {})) {
        if (normalizeAcctName(name) === target) { txns = arr; break; }
      }
    }
    txns = txns || [];
    return {
      transactions: txns.slice(0, 50),   // cap render; total kept for overflow hint
      hasSync: true,
      accountId,
      total: txns.length,
    };
  }
  return {
    transactions: [],
    hasSync: false,
    accountId,
    ctaMessage: 'Transaction drill-in pending — will be wired to Cleic Ledger in a future phase.',
  };
}

/* ---------- FORMATTING HELPERS ---------- */
export function fmtMoney(n, opts = {}) {
  return formatMoney(n, { currencyCode: opts.currencyCode, decimals: opts.decimals });
}

export function fmtMoneyShort(n, opts = {}) {
  return formatMoneyShort(n, { currencyCode: opts.currencyCode });
}

/* Compatibility aliases only. New/reusable Lens code should import fmtMoney
   and fmtMoneyShort so presentation formatting is not PHP-named by default. */
export function fmtPHP(n, opts = {}) {
  return fmtMoney(n, opts);
}

export function fmtPHPShort(n, opts = {}) {
  return fmtMoneyShort(n, opts);
}

export function fmtPct(n, decimals = 1) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

/* v8 — Plain-number formatter with thousand separators (#,###.##).
   Use this for non-currency numeric displays (counts, tenant totals,
   magnitudes without a currency prefix). For currency, keep using fmtMoney.
   Default decimals=2 to match the app's #,###.00 convention. */
export function fmtNum(n, decimals = 2) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return formatPlainNumber(n, { decimals });
}

export function ymAdd(ym, months) {
  const [y, m] = ym.split('-').map(Number);
  const total = y * 12 + (m - 1) + months;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/* v8 — ISO 8601 per E-002 (ecosystem-wide ADR).
   Date-only display uses yyyy-MM-dd; monthly display uses yyyy-MM.
   `ymLabel` historically returned "Apr '26"; now returns "2026-04" so
   every period header, launch date, and forecast column in the app
   conforms to the ADR. Short vintage labels ("Apr '26") were more
   scannable but shadowed the ISO standard; standardizing now. */
export function ymLabel(ym) {
  if (!ym) return '—';
  const [y, m] = String(ym).split('-').map(Number);
  if (!y || !m) return String(ym);
  return `${y}-${String(m).padStart(2, '0')}`;
}

/* v8 — normalize legacy per-tab maps ({cpa:1,cma:3,...} / {actuals:1,budgets:3,...})
   or bad values to scalar. Used everywhere the app reads formalPeriodCount /
   formalPeriodDensity so old saved profiles don't break multiplication math.
   Legacy key names preserved in the example so pre-Phase-2 profiles still parse. */
export function normalizePeriodScalar(v, fallback) {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  if (v && typeof v === 'object') {
    const vals = Object.values(v).filter(n => typeof n === 'number' && Number.isFinite(n) && n > 0);
    if (vals.length) return Math.max(...vals);
  }
  return fallback;
}

/* v8 — The single global forecast horizon in months, derived from the
   user's Period × Density picker in the ScenarioBar. Every chart, report
   table, sensitivity analysis, and forecast in the app reads from this
   one function, so changing the picker affects everything simultaneously. */
export function getEffectiveHorizon(scenario) {
  const count   = normalizePeriodScalar(scenario?.formalPeriodCount, 12);
  const density = normalizePeriodScalar(scenario?.formalPeriodDensity, 1);
  return count * density;
}

/* v8 — Period density labeler. Given the LAST month of a window + the
   density (how many months per period), return a friendly ISO-ish label.
   Density values are fixed: 1/2/3/4/6/12 months per period.

      density  |  name           |  label example
      ---------+-----------------+----------------
      1        |  Monthly        |  2026-04
      2        |  Bi-monthly     |  2026-04 (last month of window)
      3        |  Quarterly      |  2026-Q2
      4        |  4-Monthly      |  2026-04 (last month of window)
      6        |  Half-yearly    |  2026-H1
      12       |  Yearly         |  2026
*/
export function fmtPeriod(ym, density) {
  if (!ym || !density || density <= 1) return ymLabel(ym);
  const [y, m] = String(ym).split('-').map(Number);
  if (!y || !m) return ymLabel(ym);
  if (density === 3)  return `${y}-Q${Math.ceil(m / 3)}`;
  if (density === 6)  return `${y}-H${m <= 6 ? 1 : 2}`;
  if (density === 12) return String(y);
  return ymLabel(ym);
}

/* v8 — Period-density label for the picker UI. */
export const DENSITY_LABELS = {
  1:  'Monthly',
  2:  'Bi-monthly',
  3:  'Quarterly',
  4:  '4-Monthly',
  6:  'Half-yearly',
  12: 'Yearly',
};

/* v8 — Aggregate a monthly-row array into density-grouped periods.
   `mode`:
     - 'sum'  — numeric fields summed across window (flows: IS, CF)
     - 'last' — take the last month's values (stocks: BS)
   `ym` and `label` are always taken from the last month in each window so
   period headers read "2026-Q2" etc. when paired with fmtPeriod. Incomplete
   trailing windows are dropped. */
export function aggregateByDensity(rows, density, mode = 'sum') {
  if (!Array.isArray(rows) || rows.length === 0 || density <= 1) return rows;
  const out = [];
  for (let i = 0; i + density <= rows.length; i += density) {
    const window = rows.slice(i, i + density);
    if (mode === 'last') {
      out.push({ ...window[window.length - 1] });
      continue;
    }
    const merged = {};
    for (const row of window) {
      for (const [k, v] of Object.entries(row)) {
        if (typeof v === 'number') merged[k] = (merged[k] ?? 0) + v;
        else if (merged[k] === undefined) merged[k] = v;
      }
    }
    const last = window[window.length - 1];
    merged.ym = last.ym;
    merged.label = last.label;
    out.push(merged);
  }
  return out;
}

export function currentPeriodStateForRows(rows, density = 1, opts = {}) {
  const current = opts.currentYM ?? currentYM();
  const d = Math.max(1, Math.floor(normalizePeriodScalar(density, 1)));
  const fallbackLabel = fmtPeriod(current, d);
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      label: null,
      found: false,
      fallbackLabel,
      posture: 'runtime-current-absent',
      currentYM: current,
      density: d,
      index: -1,
      sourceYMRange: null,
    };
  }

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    const endYM = row?.ym ?? row?.label;
    if (!endYM) continue;
    const end = ymLabel(endYM);
    const start = ymAdd(end, -(d - 1));
    if (current >= start && current <= end) {
      return {
        label: fmtPeriod(end, d),
        found: true,
        fallbackLabel,
        posture: 'runtime-current-backed-by-rows',
        currentYM: current,
        density: d,
        index,
        sourceYMRange: { start, end },
      };
    }
  }

  return {
    label: null,
    found: false,
    fallbackLabel,
    posture: 'runtime-current-absent',
    currentYM: current,
    density: d,
    index: -1,
    sourceYMRange: null,
  };
}

export function currentPeriodLabelForRows(rows, density = 1) {
  return currentPeriodStateForRows(rows, density).label;
}

/* Format a date-only value (Date object, ISO string, or epoch ms) as
   yyyy-MM-dd. Returns '—' for nullish. Used everywhere a calendar date
   is displayed in the UI — replaces any ad-hoc toLocaleDateString calls
   per E-002 Rule 4 (display at UI boundary only, no locale formatting
   in service code). */
export function fmtDate(value) {
  return formatDateOnly(value);
}

export function monthsBetween(startYM, endYM) {
  const [sy, sm] = startYM.split('-').map(Number);
  const [ey, em] = endYM.split('-').map(Number);
  return (ey * 12 + em) - (sy * 12 + sm);
}

export function currentYM() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/* ---------- SALARY CALC ---------- */
export function calcFortnightlyGross() {
  return SALARY.rateHourly * SALARY.hoursPerDay * SALARY.workingDaysPerFortnight;
}
export function calcMonthlyGross() {
  return calcFortnightlyGross() * 2;
}
export function calcMonthlyWHT(bracket = 'mid') {
  return SALARY.withholdingTaxPerPay[bracket] * 2;
}
export function calcMonthlyGovContribs() {
  return SALARY.govContributions.reduce((sum, c) => sum + c.amount + c.bankFee, 0);
}
export function calcMonthlyNet(whtBracket = 'mid') {
  return calcMonthlyGross() - calcMonthlyWHT(whtBracket) - calcMonthlyGovContribs();
}

/* ---------- RECURRING EXPENSE CALC ---------- */
/* v7.2 — Piecewise recurring schedule.
   schedule = {
     startYM?: 'YYYY-MM',             // optional — line not active before this
     stopYM?:  'YYYY-MM',             // optional — line stops at / after this
     steps?:   [{ fromYM, amount, note? }]  // optional — amount changes
   }
   Resolution:
   - If ym < startYM → 0 (not yet active).
   - If ym >= stopYM → 0 (stopped).
   - If steps present → use the step where fromYM is the latest ≤ ym.
   - Otherwise → null (caller falls back to flat amount).
*/
export function resolveScheduledAmount(schedule, ym, fallback) {
  if (!schedule || !ym) return fallback;
  if (schedule.startYM && ym < schedule.startYM) return 0;
  if (schedule.stopYM  && ym >= schedule.stopYM) return 0;
  const steps = Array.isArray(schedule.steps) ? schedule.steps : null;
  if (steps && steps.length > 0) {
    let chosen = null;
    for (const s of steps) {
      if (!s.fromYM || s.fromYM <= ym) chosen = s;
    }
    if (chosen != null) return Number(chosen.amount);
  }
  return fallback;
}

/* v7.2 — scenario-level recurring overrides, keyed by line.code.
   Lets the operator override a baked-in schedule without mutating data.js. */
export function resolveLineSchedule(line, scenario) {
  const key = line?.code;
  const override = scenario?.recurringOverrides?.[key]?.schedule;
  return override ?? line?.schedule ?? line?.defaultSchedule ?? null;
}

/* v7.2 — custody stream effective amount via schedule. Backward compat:
   scenario.payDirectEnabled === false still zeroes it (kill switch). */
export function calcJMPayDirectForMonth(scenario, ym) {
  if (scenario?.payDirectEnabled === false) return 0;
  const line = INCOMES.jmPayDirect;
  if (!line || !ym) return 0;
  const schedule = resolveLineSchedule(line, scenario);
  if (!schedule) return 0;
  if (schedule.startYM && ym < schedule.startYM) return 0;
  if (schedule.stopYM  && ym >= schedule.stopYM) return 0;
  const steps = Array.isArray(schedule.steps) ? schedule.steps : null;
  if (steps && steps.length > 0) {
    let chosen = null;
    for (const s of steps) {
      if (!s.fromYM || s.fromYM <= ym) chosen = s;
    }
    if (chosen != null) return Number(chosen.amount);
  }
  return 0;
}

/* v5/v7.2 — effective PHP amount (flat fallback path).
   Does NOT walk schedule — effectivePHPAmount handles schedule first to avoid
   double-resolution. This stays simple: downgrade shim + flat amount. */
export function effectiveRecurringAmount(r, ym) {
  if (!ym) return r.amount;
  if (r.downgradeYM && ym >= r.downgradeYM && r.downgradeAmount != null) {
    return r.downgradeAmount;
  }
  return r.amount;
}

/* v7.1 — FX rate resolution.
   Resolution order (highest wins):
     1. scenario.fxRates[currency]   — manual user override
     2. fx.baseRate × (1 + fx.monthlyDrift)^monthsSinceSnapshot
     3. 1.0                          — no FX (PHP) */
export function resolveFXRate(fx, scenario, ym) {
  if (!fx || !fx.currency) return 1;
  const override = scenario?.fxRates?.[fx.currency];
  if (typeof override === 'number' && Number.isFinite(override)) return override;
  const base = Number(fx.baseRate ?? 1);
  const drift = Number(fx.monthlyDrift ?? 0);
  const months = ym ? monthsBetween(SNAPSHOT_AS_OF.slice(0, 7), ym) : 0;
  return base * Math.pow(1 + drift, Math.max(0, months));
}

/* v7.1/v7.2 — PHP-equivalent for a recurring/non-recurring line at a given ym.
   Resolution order (highest wins):
     1. scenario.recurringOverrides[line.code].schedule  → step/start/stop (PHP by convention)
     2. line.schedule                                    → step/start/stop (PHP by convention)
     3. line.fx.native × resolveFXRate                   → FX-priced flat amount
     4. effectiveRecurringAmount (legacy downgrade shim + flat r.amount)
   By convention schedule.steps[].amount is always in PHP — the step represents
   a moment where the operator pins a specific PHP value (often after a pricing change or
   currency switch like Google One's 2TB→Basic tier migration). Any step can
   include `native` + `fx` if re-pricing in FX needed. */
export function effectivePHPAmount(r, ym, scenario) {
  if (!ym) return r.amount;
  const schedule = resolveLineSchedule(r, scenario);
  if (schedule) {
    if (schedule.startYM && ym < schedule.startYM) return 0;
    if (schedule.stopYM  && ym >= schedule.stopYM) return 0;
    const steps = Array.isArray(schedule.steps) ? schedule.steps : null;
    if (steps && steps.length > 0) {
      let chosen = null;
      for (const s of steps) {
        if (!s.fromYM || s.fromYM <= ym) chosen = s;
      }
      if (chosen != null) {
        /* Step explicitly pinned in foreign currency (uncommon) */
        if (chosen.fx?.native != null) {
          const rate = resolveFXRate(chosen.fx, scenario, ym);
          return Number(chosen.fx.native) * rate;
        }
        return Number(chosen.amount);
      }
    }
  }
  /* No schedule (or schedule had no applicable step) — fall through to FX flat */
  if (r?.fx && r.fx.native != null) {
    const rate = resolveFXRate(r.fx, scenario, ym);
    return Number(r.fx.native) * rate;
  }
  return effectiveRecurringAmount(r, ym);
}

/* v7.1 — collect all distinct FX currencies used by recurring + (future) other lines.
   Used by ScenarioPanel FX override section. */
export function detectFXCurrencies() {
  const set = new Set();
  const add = (r) => { if (r?.fx?.currency) set.add(r.fx.currency); };
  RECURRING.monthly.forEach(add);
  RECURRING.yearly.forEach(add);
  RECURRING.quarterly.forEach(add);
  return Array.from(set).sort();
}

/* v7.1 — fxHint object for MoneyCell `fxHint` prop. Returns null when
   line has no fx so callers can spread `{...(fxHintFor(r,scenario,ym))}`
   without conditional. */
export function fxHintFor(r, scenario, ym) {
  if (!r?.fx || r.fx.native == null) return null;
  const rate = resolveFXRate(r.fx, scenario, ym);
  return {
    native: `${r.fx.currency} ${fmtNum(Number(r.fx.native), 2)}`,
    rate:   `${rate.toFixed(2)}`,
  };
}

/* ---------- v6 — PIECEWISE PAYMENT SCHEDULE HELPERS ----------
   A "schedule" = [{ date:'YYYY-MM-DD', amount, account? }, ...].
   Resolution order (highest wins):
     1. scenario.piecewiseOverrides[lineKey]  (user override, optional null = revert)
     2. line.paymentSchedule                  (data.js baked-in default schedule)
     3. defaultMonthly                        (single monthly amortized fallback)
*/
export function piecewiseAmountInMonth(schedule, ym) {
  if (!Array.isArray(schedule) || schedule.length === 0) return 0;
  return schedule.reduce((s, r) => {
    const rYM = (r.date || '').slice(0, 7);
    return rYM === ym ? s + (Number(r.amount) || 0) : s;
  }, 0);
}
/* Resolve which schedule to use given a line + a scenario map. */
export function resolveSchedule(line, lineKey, scenarioOverrides) {
  if (scenarioOverrides && Object.prototype.hasOwnProperty.call(scenarioOverrides, lineKey)) {
    return scenarioOverrides[lineKey];   // may be null → forced default
  }
  if (line && Array.isArray(line.paymentSchedule) && line.paymentSchedule.length > 0) {
    return line.paymentSchedule;
  }
  return null;
}
export function hasResolvedPiecewise(line, lineKey, scenarioOverrides) {
  const s = resolveSchedule(line, lineKey, scenarioOverrides);
  return Array.isArray(s) && s.length > 0;
}
/* Legacy helper for callers that don't yet pass a scenario. */
export function hasPiecewise(line) {
  return line && Array.isArray(line.paymentSchedule) && line.paymentSchedule.length > 0;
}
/* Core routing — preferred (scenario-aware) signature. */
export function calcLineForMonth(line, ym, defaultMonthly, scenarioOverrides, lineKey) {
  const s = resolveSchedule(line, lineKey, scenarioOverrides);
  if (Array.isArray(s) && s.length > 0) return piecewiseAmountInMonth(s, ym);
  return defaultMonthly;
}
/* Validate a schedule sums to an expected annual/target amount (±0.005). */
export function validatePiecewise(schedule, expectedTotal) {
  const sum = (schedule || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  return {
    sum,
    target: expectedTotal,
    delta: sum - expectedTotal,
    valid: Math.abs(sum - expectedTotal) < 0.005,
  };
}

/* v6 — stable line keys for scenario.piecewiseOverrides lookup. */
export function recurringKey(r) {
  return `r:${r.code || 'NA'}:${r.vendor || ''}`;
}
export const FAMILY_DIRECT_KEY = 'familyDirect';
export const MARIAN_KEY        = 'marian';

export function calcMonthlyRecurringSubs(ym = null, scenario = null) {
  const overrides = scenario?.piecewiseOverrides;
  return RECURRING.monthly
    /* v7.2 — forecastOnly items still contribute to totals; badge shows source */
    .reduce((sum, r) => {
      /* v7.1 — FX-aware fallback for default monthly */
      const def = effectivePHPAmount(r, ym, scenario);
      return sum + (ym ? calcLineForMonth(r, ym, def, overrides, recurringKey(r)) : def);
    }, 0);
}

/* Drill-down contributors: every monthly/yearly/quarterly line with its monthly eq.
   v6 — flags piecewise overrides in the `source` field so drill-downs show intent.
   v7.1 — FX-aware: PHP equivalent computed at evaluation time when fx.native is set. */
export function recurringBreakdown(ym = null, scenario = null) {
  const overrides = scenario?.piecewiseOverrides;
  const rows = [];
  /* v7.2 — include forecastOnly items in breakdown; badge shows source */
  for (const r of RECURRING.monthly) {
    const def = effectivePHPAmount(r, ym, scenario);
    const key = recurringKey(r);
    const has = hasResolvedPiecewise(r, key, overrides);
    const amt = ym ? calcLineForMonth(r, ym, def, overrides, key) : def;
    const fxTag = r.fx?.native != null ? ` · ${r.fx.currency} ${r.fx.native}` : '';
    const forecastTag = r.forecastOnly ? ' · [FORECAST]' : '';
    rows.push({ label: r.vendor, amount: amt, source: (has ? `${r.code} · piecewise` : `${r.code} · monthly`) + fxTag + forecastTag, cat: r.cat, forecastOnly: !!r.forecastOnly });
  }
  for (const r of RECURRING.yearly) {
    const key = recurringKey(r);
    const has = hasResolvedPiecewise(r, key, overrides);
    const annualPHP = effectivePHPAmount(r, ym, scenario);
    const amt = has && ym
      ? piecewiseAmountInMonth(resolveSchedule(r, key, overrides), ym)
      : annualPHP / 12;
    const fxTag = r.fx?.native != null ? ` · ${r.fx.currency} ${r.fx.native}/yr` : '';
    rows.push({ label: r.vendor, amount: amt, source: (has ? `${r.code} · yearly-piecewise` : `${r.code} · yearly÷12`) + fxTag, cat: r.cat });
  }
  for (const r of RECURRING.quarterly) {
    const key = recurringKey(r);
    const has = hasResolvedPiecewise(r, key, overrides);
    const quarterlyPHP = effectivePHPAmount(r, ym, scenario);
    const amt = has && ym
      ? piecewiseAmountInMonth(resolveSchedule(r, key, overrides), ym)
      : (quarterlyPHP * 4) / 12;
    rows.push({ label: r.vendor, amount: amt, source: has ? `${r.code} · quarterly-piecewise` : `${r.code} · quarterly×4÷12`, cat: r.cat });
  }
  return rows.sort((a, b) => b.amount - a.amount);
}
export function calcYearlySubsAmortized(ym = null, scenario = null) {
  const overrides = scenario?.piecewiseOverrides;
  return RECURRING.yearly.reduce((sum, r) => {
    const key = recurringKey(r);
    if (hasResolvedPiecewise(r, key, overrides) && ym) {
      return sum + piecewiseAmountInMonth(resolveSchedule(r, key, overrides), ym);
    }
    return sum + effectivePHPAmount(r, ym, scenario) / 12;
  }, 0);
}
export function calcQuarterlySubsAmortized(ym = null, scenario = null) {
  const overrides = scenario?.piecewiseOverrides;
  return RECURRING.quarterly.reduce((sum, r) => {
    const key = recurringKey(r);
    if (hasResolvedPiecewise(r, key, overrides) && ym) {
      return sum + piecewiseAmountInMonth(resolveSchedule(r, key, overrides), ym);
    }
    return sum + (effectivePHPAmount(r, ym, scenario) * 4) / 12;
  }, 0);
}
export function calcMonthlyAllRecurring(ym = null, scenario = null) {
  return calcMonthlyRecurringSubs(ym, scenario)
       + calcYearlySubsAmortized(ym, scenario)
       + calcQuarterlySubsAmortized(ym, scenario);
}
export function yearlySubsForMonth(monthNum) {
  return RECURRING.yearly.filter(r => r.month === monthNum)
    .reduce((sum, r) => sum + r.amount, 0);
}
/* Split for charting — v7.2: include forecastOnly items */
export function recurringByCategory() {
  const byCat = {};
  for (const r of RECURRING.monthly) {
    byCat[r.cat] = (byCat[r.cat] || 0) + r.amount;
  }
  for (const r of RECURRING.yearly) {
    byCat[r.cat] = (byCat[r.cat] || 0) + r.amount / 12;
  }
  for (const r of RECURRING.quarterly) {
    byCat[r.cat] = (byCat[r.cat] || 0) + r.amount * 4 / 12;
  }
  return Object.entries(byCat).map(([name, value]) => ({ name, value }));
}

/* ---------- SUPPORT AP — v4 (direct + routed), v6 piecewise-aware ---------- */
/* Default monthly value (pre-piecewise, pre-inflation) */
export function calcMonthlyFamilyDirectDefault() {
  return FAMILY_AP.direct.monthlyAmount;
}
/* Cash-flow amount for a given month — piecewise if schedule exists */
export function calcMonthlyFamilyDirect(ym = null, scenario = null) {
  const def = calcMonthlyFamilyDirectDefault();
  if (!ym) return def;
  return calcLineForMonth(FAMILY_AP.direct, ym, def, scenario?.piecewiseOverrides, FAMILY_DIRECT_KEY);
}
export function calcMonthlyFamilyDirectChannels() {
  return FAMILY_AP.direct.channels.map(c => ({ ...c }));
}
/* Default Marian monthly (pre-piecewise) — honors the bump window */
export function calcMonthlyMarianDefault(ym) {
  const { bumpedYM, monthlyAmount, previousAmount } = FAMILY_AP.viaMarian;
  if (!ym || ym >= bumpedYM) return monthlyAmount;
  return previousAmount;
}
export function calcMonthlyMarian(ym, scenario = null) {
  const def = calcMonthlyMarianDefault(ym);
  if (!ym) return def;
  return calcLineForMonth(FAMILY_AP.viaMarian, ym, def, scenario?.piecewiseOverrides, MARIAN_KEY);
}
export function marianArrears() {
  return FAMILY_AP.viaMarian.arrears;
}

/* Total monthly AP outbound to family system */
export function calcMonthlyFamilyAP(ym, scenario = null) {
  return calcMonthlyFamilyDirect(ym, scenario) + calcMonthlyMarian(ym, scenario);
}

/* ---------- AR — v4 with contributor toggles, v6 custom amount ---------- */
/* Model assumptions:
     scenario.jpMode   = 'current' (₱3,500) | 'historical' (₱5,000) | 'custom' (jpAmount)
     scenario.jpAmount = editable custom (used only when jpMode === 'custom')
     scenario.jcAmount = editable (default 2,500)
     scenario.jmAmount = editable (default 2,000)
   collectionRate is applied to get EXPECTED cash, but scheduled AR stays full.
*/
function resolveJPAmount(scenario, fallback) {
  if (scenario.jpMode === 'historical') return 5000;
  if (scenario.jpMode === 'custom')     return scenario.jpAmount ?? fallback;
  return fallback;  // 'current' default → AR line amount (3,500)
}
export function calcScheduledEducationAR(scenario) {
  let total = 0;
  for (const s of AR_RECURRING.educationFund) {
    if (s.contactId === 'demo-contact-3') {
      total += resolveJPAmount(scenario, s.amount);
    } else if (s.contactId === 'demo-contact-4') {
      total += scenario.jcAmount ?? s.amount;
    } else if (s.contactId === 'demo-contact-5') {
      total += scenario.jmAmount ?? s.amount;
    } else {
      total += s.amount;
    }
  }
  return total;
}
export function calcExpectedEducationAR(scenario) {
  /* Apply collectionRate to each configured receivable stream. */
  let total = 0;
  for (const s of AR_RECURRING.educationFund) {
    let amt;
    if (s.contactId === 'demo-contact-3') {
      amt = resolveJPAmount(scenario, s.amount);
    } else if (s.contactId === 'demo-contact-4') {
      amt = scenario.jcAmount ?? s.amount;
    } else if (s.contactId === 'demo-contact-5') {
      amt = scenario.jmAmount ?? s.amount;
    } else {
      amt = s.amount;
    }
    total += amt * (s.collectionRate ?? 1);
  }
  return total;
}

export function calcMonthlyFamilyPoolAR(ym) {
  const base = AR_RECURRING.familyAllowance[0];
  /* Demo support pool bumps after offset ends (2026-10) per note. */
  if (ym && ym > '2026-10') return base.amount + 2000;
  return base.amount;
}

export function calcMonthlyAR(scenario, ym, opts = {}) {
  const eduFn = opts.expected ? calcExpectedEducationAR : calcScheduledEducationAR;
  return eduFn(scenario) + calcMonthlyFamilyPoolAR(ym);
}

/* Net Education Burden = routed support monthly - Education AR. */
export function netEducationBurden(scenario, ym) {
  return calcMonthlyMarian(ym, scenario) - calcExpectedEducationAR(scenario);
}

/* v8 Path I — Financial Ratios pack. Corporate-standard ratios adapted
   to operator / small-entity finance. Adds liquidity, solvency, profitability,
   and efficiency lenses beyond the DSO/DPO/CCC trio. Used by CMA's
   Financial Ratios sub-view.

   v8 Path J — TIMELESS. Every ratio now accepts a `snapshot` override
   (liquid/short-term debt/LT/equity/netIncome/revenue/monthlyAR/monthlyAP per a
   specific point in time), so the SAME ratio definitions can render for
   past months (from backcast), current, or forward forecasts. The
   companion `buildFinancialRatiosSeries` below reuses this to emit a
   5-point trend (−3mo, −1mo, now, +1mo, +3mo) for tiny sparklines.

   Keeps the shape backwards-compatible: calling with just (scenario)
   still produces the current-month snapshot. */
export function buildFinancialRatios(scenario, snapshot = null) {
  const ym = snapshot?.ym || currentYM();
  const liquid      = snapshot?.liquid      ?? totalAssetsLiquid();
  const ccDebt      = snapshot?.ccDebt      ?? totalCreditCardDebt();
  const ltl         = snapshot?.ltl         ?? totalLongTermLiabilities();
  const eq          = snapshot?.eq          ?? totalEquity();
  const netIncome   = snapshot?.netIncome   ?? buildIncomeStatement(scenario).netIncome;
  const revenue     = snapshot?.revenue     ?? (buildIncomeStatement(scenario).revenue + buildIncomeStatement(scenario).fvf);
  const totalExp    = snapshot?.totalExp    ?? buildIncomeStatement(scenario).totalExpenses;
  const fixedNet    = snapshot?.fixedNet    ?? totalFixedAssetsNet();
  const interestExp = snapshot?.interestExp ?? buildIncomeStatement(scenario).mortgagesInterestPortion;
  const totalAssets = liquid + fixedNet;
  const totalLiab   = ccDebt + ltl;

  const safe = (n, d) => (d && Number.isFinite(d) && d !== 0 ? n / d : null);
  const toneGood    = (v, thresh) => v == null ? 'neutral' : v >= thresh ? 'good' : 'bad';
  const toneBadHigh = (v, thresh) => v == null ? 'neutral' : v <= thresh ? 'good' : 'bad';

  const dso = snapshot?.dso ?? calcDSO();
  const dpo = snapshot?.dpo ?? calcDPO(scenario);
  const ccc = dso - dpo;
  /* v8 Path AF.3 — per-row Cash runway.
     Previously the snapshot builder passed `runway: calcRunway(scenario)`,
     which ran a 48-month forward projection and always returned the
     SAME number for every horizon cell. Worse: if liquid never hit
     zero in 48 months, `calcRunway` returned Infinity → fmtMetric
     saw !Number.isFinite and rendered em-dash. Every period looked
     empty even though the number was positive (just very large).

     Definition match: the Cash runway explanation asks how many months
     current liquidity can cover operating burn → `liquid ÷ monthly
     expenses`. Both inputs already
     live on the snapshot (liquid = period-ending cash, totalExp =
     period's total expenses incl. interest), and both naturally
     vary per period.

     Live "now" card (BudgetsTab) keeps calling `buildFinancialRatios(scenario)`
     without a snapshot, so the full forward-projecting calcRunway
     path stays available — we just stop using it for the horizon. */
  const runway = snapshot
    ? safe(liquid, totalExp)           // per-period income-stops runway
    : calcRunway(scenario);            // forward-projected (current only)

  /* Monthly AR/AP for turnover ratios (annualized). Kept for other
     callers that read these fields — turnover itself is derived
     purely from DSO/DPO below. */
  const monthlyAR = snapshot?.monthlyAR ?? (calcExpectedEducationAR(scenario) + calcMonthlyFamilyPoolAR(ym));
  const monthlyAP = snapshot?.monthlyAP ?? calcMonthlyFamilyAP(ym, scenario);

  /* v8 Path AF.2 — turnover math, corrected.
     Previous formula: `safe(monthlyAR * 12, monthlyAR * (dso / 365))`
     algebraically reduced to `12 × 365 / dso = 4380/dso`, producing
     values 12× larger than the textbook standard (AR turnover of
     1095× at DSO=4d, when the correct answer is ~91×).

     Standard definition: AR turnover = annual revenue ÷ avg AR.
     Under the steady-state identity `AR = (DSO/365) × annual_revenue`,
     the ratio simplifies to `365/DSO`. This closed form is exactly
     how working-capital analysts quote turnover alongside DSO — the
     two numbers are two faces of the same coin (inverse relationship,
     scaled by 365). Same logic for AP/DPO. */
  const arTurnover = dso > 0 ? 365 / dso : null;
  const apTurnover = dpo > 0 ? 365 / dpo : null;

  return [
    /* === LIQUIDITY === */
    {
      category: 'Liquidity',
      ratio: 'Cash ratio',
      formula: 'Liquid cash / short-term obligations',
      value: safe(liquid, ccDebt),
      format: 'ratio',
      betterWhen: 'higher',   /* more liquid relative to short-term debt = stronger */
      tone: (() => { const v = safe(liquid, ccDebt); return toneGood(v, 1.0); })(),
      hint: 'Liquid cash / short-term obligations. Healthy: >=1.0. Strong: >=2.0.',
      explanation: 'The strictest liquidity lens: if short-term obligations came due now, could available cash cover them? A ratio of 1.0 means exact parity. Below 1.0 signals reliance on asset sales, receivable collection, or new financing. Benchmarks vary by operating model; use trend and obligation cadence as the primary read.',
      direction: {
        whenUp: 'Liquid cash growing faster than short-term debt — buffer strengthening.',
        whenDown: 'Short-term debt outpacing liquid cash — cushion thinning.',
      },
    },
    {
      category: 'Liquidity',
      ratio: 'Current ratio (proxy)',
      formula: '(Liquid + expected AR) / (short-term obligations + monthly AP)',
      value: safe(liquid + monthlyAR, ccDebt + monthlyAP),
      format: 'ratio',
      betterWhen: 'higher',
      direction: {
        whenUp: 'Near-cash resources covering near-term obligations more comfortably.',
        whenDown: 'Upcoming payables catching up to available cash + AR — pinch point approaching.',
      },
      tone: (() => { const v = safe(liquid + monthlyAR, ccDebt + monthlyAP); return toneGood(v, 1.5); })(),
      hint: '(Liquid + expected AR) / (short-term obligations + monthly AP). 1.5-2.0 is healthy.',
      explanation: 'Broader than cash ratio because expected receivables count as near-cash. A ratio of 1.5 means near-cash resources cover 1.5x near-term obligations. Above 3.0 can signal idle capital; below 1.0 flags a liquidity pinch. This is a common lender and operator working-capital lens.',
    },
    {
      category: 'Liquidity',
      ratio: 'Cash runway',
      formula: 'Liquid ÷ monthly burn',
      value: runway,
      format: 'months',
      betterWhen: 'higher',   /* more months of covered burn = safer */
      direction: {
        whenUp: 'More months of operating burn covered by current liquidity.',
        whenDown: 'Runway shrinking — operating cushion tightening.',
      },
      tone: runway > 12 ? 'good' : runway > 6 ? 'warn' : 'bad',
      hint: 'Months of operating burn covered by current liquid. ≥12mo is comfortable.',
      explanation: 'Operating runway answers: how many months can current liquidity cover recurring burn if inflows pause? Six months is a minimum cushion, 12 months is comfortable, and 24+ months creates strategic room. Runs the forecast engine, not a static snapshot, so expected model changes are included.',
    },
    /* === SOLVENCY === */
    {
      category: 'Solvency',
      ratio: 'Debt-to-Equity',
      formula: 'Total liabilities ÷ equity',
      value: safe(totalLiab, eq),
      format: 'ratio',
      betterWhen: 'lower',    /* less leverage = lower insolvency risk */
      direction: {
        whenUp: 'Leverage increasing — downside shocks will hit equity harder.',
        whenDown: 'Deleveraging — equity cushion growing, safer in a downturn. NOTE: when equity is negative (D/E shows negative), the ratio is signaling insolvency rather than leverage; focus on "equity trending toward positive" instead.',
      },
      tone: (() => { const v = safe(totalLiab, eq); return toneBadHigh(v, 1.5); })(),
      hint: 'Total liabilities ÷ equity. <1 = conservative, >2 = highly leveraged.',
      explanation: 'How leveraged the balance sheet is. D/E of 0 = zero debt, 0.5 = debt equals half of equity, 1.0 = parity, and 2.0+ is highly leveraged. Asset-heavy books naturally run higher because term debt can inflate liabilities; compare against peers and operating model, not a single universal threshold.',
    },
    {
      category: 'Solvency',
      ratio: 'Debt-to-Assets',
      formula: 'Total liabilities ÷ total assets',
      value: safe(totalLiab, totalAssets),
      format: 'pct',
      betterWhen: 'lower',    /* smaller share of assets financed by debt */
      direction: {
        whenUp: 'Creditors finance a larger share of assets — equity buffer eroding.',
        whenDown: 'Equity finances more of the asset base — cushion thickening.',
      },
      tone: (() => { const v = safe(totalLiab, totalAssets); return toneBadHigh(v, 0.5); })(),
      hint: 'What fraction of assets is financed by debt. <50% is healthy.',
      explanation: 'What fraction of assets is financed by creditors. 0% means assets are fully equity-funded; 50% means liabilities finance half the asset base. Above 70% can create technical insolvency risk during drawdowns. The inverse (1 − D/A) is the equity cushion before debt exceeds assets.',
    },
    {
      category: 'Solvency',
      ratio: 'Interest coverage',
      formula: '(Net income + interest) ÷ interest expense',
      value: safe(netIncome + interestExp, interestExp),
      format: 'ratio',
      betterWhen: 'higher',   /* cover interest more times = safer */
      direction: {
        whenUp: 'Earnings covering interest more comfortably — debt service getting easier.',
        whenDown: 'Interest eating a bigger slice of earnings — debt-on-debt spiral risk.',
      },
      tone: (() => { const v = safe(netIncome + interestExp, interestExp); return toneGood(v, 3); })(),
      hint: '(Net income + interest) ÷ interest expense. ≥3× = comfortable.',
      explanation: 'How many times earnings can cover interest. 1.0 means interest is just covered, leaving nothing for principal. 3.0 is a common lender comfort threshold; 5.0+ is strong. Below 1.0 means interest is being funded from reserves or additional borrowing.',
    },
    /* === PROFITABILITY === */
    {
      category: 'Profitability',
      ratio: 'Savings rate',
      formula: 'Net income ÷ gross income',
      value: safe(netIncome, revenue),
      format: 'pct',
      betterWhen: 'higher',   /* retain more earned capacity */
      direction: {
        whenUp: 'Keeping a larger share of income — every 1% compounds into years off retirement.',
        whenDown: 'Spending is creeping up relative to income — wealth-building slowing.',
      },
      tone: (() => { const v = safe(netIncome, revenue); return toneGood(v, 0.20); })(),
      hint: 'Net income / gross income. General planning target 10-20%, operator target 25%+.',
      explanation: 'A durable household, operator, or small-business finance metric. Every 1% higher means more retained capacity. Many planning models target 10-20%; more aggressive capital-building plans can target 25%+. Trend matters more than point value because steady increases compound powerfully.',
    },
    {
      category: 'Profitability',
      ratio: 'Expense ratio',
      formula: 'Total expenses ÷ gross income',
      value: safe(totalExp, revenue),
      format: 'pct',
      betterWhen: 'lower',    /* inverse of savings rate — less spent = more saved */
      direction: {
        whenUp: 'A larger share of income going to expenses — recurring spend drift.',
        whenDown: 'Tighter expense discipline — more income available to save or invest.',
      },
      tone: (() => { const v = safe(totalExp, revenue); return toneBadHigh(v, 0.80); })(),
      hint: 'Total expenses ÷ gross income. <80% = building wealth, >95% = precarious.',
      explanation: 'Inverse of savings rate. 100% means expenses consume all earned income; above 100% means spending is funded by reserves or debt. The fastest improvement path is usually identifying the top expense categories and testing whether each still earns its place. Recurring spend drifts up quietly; this ratio makes the drift visible.',
    },
    {
      category: 'Profitability',
      ratio: 'Return on Equity (ROE)',
      formula: 'Annualized net income ÷ equity',
      value: safe(netIncome * 12, eq),
      format: 'pct',
      betterWhen: 'higher',
      direction: {
        whenUp: 'Each peso of equity producing more return — capital working harder.',
        whenDown: 'Equity under-utilized or returns compressing — consider reallocation (investment, paydown).',
      },
      tone: (() => { const v = safe(netIncome * 12, eq); return toneGood(v, 0.15); })(),
      hint: 'Annualized net income ÷ equity. ≥15% is corporate-grade performance.',
      explanation: 'How hard equity is working. 15%+ is strong return territory; below 5% can signal under-utilized capital. Very high ROE can mean thin or negative equity rather than exceptional returns, so inspect the denominator before celebrating.',
    },
    {
      category: 'Profitability',
      ratio: 'Return on Assets (ROA)',
      formula: 'Annualized net income ÷ total assets',
      value: safe(netIncome * 12, totalAssets),
      format: 'pct',
      betterWhen: 'higher',
      direction: {
        whenUp: 'Every asset (yours + debt-financed) producing more return — operational productivity rising.',
        whenDown: 'Assets sitting idle or yields compressing — efficiency slipping.',
      },
      tone: (() => { const v = safe(netIncome * 12, totalAssets); return toneGood(v, 0.05); })(),
      hint: 'Annualized net income ÷ total assets. Measures how hard assets work.',
      explanation: 'Similar to ROE but includes debt-financed assets. Always ≤ ROE. The gap (ROE − ROA) reveals how much return comes from leverage vs productive assets. If ROE is 20% and ROA is 5%, three-quarters of equity return comes from leverage — useful when markets rise, dangerous when they fall.',
    },
    /* === EFFICIENCY === */
    {
      category: 'Efficiency',
      ratio: 'DSO (days to collect)',
      formula: 'Average collection days on AR',
      value: dso,
      format: 'days',
      betterWhen: 'lower',    /* collect faster = less AR float cost */
      direction: {
        whenUp: 'Taking longer to collect receivables — cash parked outside the book.',
        whenDown: 'Collections arriving sooner — cash-cycle tightening.',
      },
      tone: toneBadHigh(dso, 30),
      hint: 'Days Sales Outstanding — avg collection time on AR. ≤30 is strong.',
      explanation: 'Days Sales Outstanding — how long cash is parked in receivables before collection. 30 days aligns with common Net-30 terms; 60+ can signal collection friction. Use this to understand how receivable cadence affects cash planning.',
    },
    {
      category: 'Efficiency',
      ratio: 'DPO (days to pay)',
      formula: 'Average days to settle AP',
      value: dpo,
      format: 'days',
      betterWhen: 'neutral',  /* higher = more float, but too high damages relationships */
      tone: 'neutral',
      direction: {
        neutral: 'Higher = more vendor float. Too high damages relationships and vendor trust. Steady cadence near agreed terms (Net 15 / Net 30) is the disciplined sweet spot.',
      },
      hint: 'Days Payables Outstanding — avg days to settle AP. Higher = more float.',
      explanation: 'Days Payables Outstanding — how long bills take to settle. Higher creates working-capital float, but too high damages relationships. Net 15 is tight, Net 30 is standard, and Net 45-60 is aggressive. Compare against agreed terms and current arrears cadence.',
    },
    {
      category: 'Efficiency',
      ratio: 'CCC (cash conversion)',
      formula: 'DSO − DPO',
      value: ccc,
      format: 'days',
      betterWhen: 'lower',    /* negative CCC = collect before paying (ideal) */
      direction: {
        whenUp: 'Waiting longer to convert AR to cash after paying AP — working-capital squeeze.',
        whenDown: 'Cash cycle closing — or going NEGATIVE (Amazon-style collect-before-pay, every day is free financing).',
      },
      tone: ccc < 0 ? 'good' : 'warn',
      hint: 'DSO − DPO. Negative = collections arrive before payment obligations. Hallmark of great cash-flow businesses.',
      explanation: 'The single most important working-capital number. NEGATIVE CCC means collections arrive before related obligations come due — the hallmark of Amazon, Costco, and Dell\'s classic supply-chain model. Every day of negative CCC is a day of free financing. Positive CCC means the book floats the gap with its own cash. Dropping CCC by 10 days can meaningfully change cash runway.',
    },
    {
      category: 'Efficiency',
      ratio: 'AR turnover (annual)',
      formula: 'Annual revenue ÷ average AR',
      value: arTurnover,
      format: 'x',
      betterWhen: 'higher',   /* faster collection cycles */
      direction: {
        whenUp: 'AR book cycling through faster — less cash parked in receivables, more agility.',
        whenDown: 'Collection cycles slowing — more cash tied up, stale-invoice risk rising.',
      },
      tone: (() => { const v = arTurnover; return toneGood(v, 10); })(),
      hint: 'Revenue ÷ avg AR. Higher = faster collection cycles.',
      explanation: 'How many times the AR book "turns over" in a year. 12× means the full AR balance is collected every month. Higher is better because fast collection means less cash tied up and more agility. Low turnover (<6×) suggests collection friction: stale invoices, customer payment issues, or overly loose terms.',
    },
    {
      category: 'Efficiency',
      ratio: 'AP turnover (annual)',
      formula: 'Annual payables ÷ average AP',
      value: apTurnover,
      format: 'x',
      betterWhen: 'neutral',  /* too fast = lose float, too slow = vendor trust */
      tone: 'neutral',
      direction: {
        neutral: 'Too fast = paying before capturing vendor-float benefit. Too slow = vendor trust erodes. Inverse of DPO — steady cadence matching your Net-terms is the disciplined sweet spot.',
      },
      hint: 'Payables ÷ avg AP. Moderate values = disciplined supplier payment.',
      explanation: 'How many times AP cycles through in a year. Too high = paying too fast and leaving float on the table. Too low = paying too slowly and risking vendor trust. The sweet spot depends on industry, terms, and relationship strength.',
    },
  ];
}

/* v8 Path J — Time-aware ratio series. Produces ratios at 5 points:
   −3mo, −1mo, current, +1mo, +3mo. The past points come from the
   backcast engine; future points from the forecast engine. Both are
   mapped to the same `snapshot` shape that `buildFinancialRatios`
   accepts, so the SAME 15 ratio definitions get applied at every
   point in time. Consumers use this to draw a 5-point sparkline per
   ratio — the "timeless" feature request.

   This makes every ratio look like the 3-statement model: it rolls
   forward AND backward. Direction-picker-aware would be ideal later,
   but fixed 5-point trend gives the "timeless" feel with a simple
   renderer. */
/* v8 Path AF.1 — row-derived working capital helpers.
   Kept local (not exported) because they're only meaningful inside
   the snapshot builder — they read the forecast row's shape. If we
   later need them in other surfaces, promote + export. */
function rowDerivedDSO(row) {
  const ar = row.totalAR ?? 0;
  const revenue = (row.salaryNet ?? 0) + (row.fvfMRR ?? 0) + (row.totalAR ?? 0);
  if (revenue <= 0 || ar <= 0) return calcDSO();   // edge-case fallback
  return Math.round((ar / revenue) * 30);
}
function rowDerivedDPO(row) {
  const ap = (row.familyDirect ?? 0) + (row.marianAP ?? 0);
  const expenses = (row.subs ?? 0) + (row.familyDirect ?? 0) + (row.marianAP ?? 0) +
                   (row.mortgages ?? 0) + (row.fuel ?? 0) + (row.seasonal ?? 0) +
                   (row.lifestyle ?? 0);
  if (expenses <= 0 || ap <= 0) return 15;          // edge-case: Net 15 baseline
  return Math.round((ap / expenses) * 30);
}

export function rowToRatioSnapshot(row, scenario) {
  if (!row) return null;
  const interest = row.mortgageInterest ?? 0;
  const revenue = (row.salaryNet ?? 0) + (row.fvfMRR ?? 0) + (row.totalAR ?? 0);
  const expenses = (row.subs ?? 0) + (row.familyDirect ?? 0) + (row.marianAP ?? 0) +
                   (row.mortgages ?? 0) + (row.fuel ?? 0) + (row.seasonal ?? 0) +
                   (row.lifestyle ?? 0);
  const netIncome = revenue - expenses - interest;
  return {
    ym: row.ym,
    liquid:    row.liquidEnding ?? 0,
    ccDebt:    row.ccDebtEnding ?? 0,
    ltl:       row.mortgageOutstandingEnding ?? 0,
    eq:        (row.liquidEnding ?? 0) - (row.ccDebtEnding ?? 0) - (row.mortgageOutstandingEnding ?? 0),
    netIncome,
    revenue,
    totalExp:  expenses + interest,
    fixedNet:  row.mortgageOutstandingEnding ?? 0,  /* proxy: house value ≈ mortgage outstanding */
    interestExp: interest,
    monthlyAR: row.totalAR ?? 0,
    monthlyAP: (row.familyDirect ?? 0) + (row.marianAP ?? 0),
    /* v8 Path AF.1 — DSO/DPO computed FROM the row, not from static
       config. Previous revision threaded `row.ym` into calcDSO/calcDPO
       but the underlying data (AR_RECURRING.avgCollectionDays,
       FAMILY_AP.* in steady state) is constant month-over-month, so
       every horizon cell ended up with the same number anyway — the
       ym parameter arrived at a function whose output was invariant.

       Classic GAAP formulas:
         DSO  = (AR ÷ revenue) × 30      ← days of revenue parked in AR
         DPO  = (AP ÷ expenses) × 30     ← days of expenses floated in AP
       Both naturally vary per period because revenue, expenses, AR,
       and AP all fluctuate in the forecast. Zero-safety via `safe()`
       below. Fall back to the static config values when a period has
       no revenue/expense activity (avoids NaN columns at the
       projection edges).

       buildFinancialRatios still accepts `snapshot.dso`/`snapshot.dpo`
       overrides, so the live "now" card in BudgetsTab keeps using the
       static AR_RECURRING constant via the existing call path
       (calcDSO()/calcDPO(scenario) with no ym). Only the horizon
       builder takes the row-derived path. */
    dso: rowDerivedDSO(row),
    dpo: rowDerivedDPO(row),
    /* v8 Path AF.3 — runway REMOVED from the snapshot. Previously
       pinned to current-month calcRunway() → every cell identical
       (Infinity for the source book, rendered em-dash). buildFinancialRatios
       now computes it from this snapshot's liquid + totalExp, so
       each horizon period shows its own income-stops runway. */
  };
}

export function buildFinancialRatiosSeries(scenario) {
  /* Pull 3 past + 3 future (we use −3, −1, now, +1, +3 as sparkline pts) */
  const past = buildThreeStatementBackcast(scenario, 3);
  const fwd = buildThreeStatementProjection(scenario, 3);
  const pastRows = past.forecast || [];
  const fwdRows  = fwd.forecast || [];

  const ratiosAt = (row) => {
    const snap = rowToRatioSnapshot(row, scenario);
    return buildFinancialRatios(scenario, snap);
  };

  /* Indices — past is oldest→newest ending at snap inclusive.
     past[0] = −3mo, past[1] = −2, past[2] = −1, past[3] = snap
     fwd[0] = snap month, fwd[1] = +1, fwd[2] = +2 */
  const pm3 = pastRows[0] ? ratiosAt(pastRows[0]) : null;
  const pm1 = pastRows[2] ? ratiosAt(pastRows[2]) : null;
  const current = buildFinancialRatios(scenario);   /* actual current — not synthesized */
  const fp1 = fwdRows[1] ? ratiosAt(fwdRows[1]) : null;
  const fp3 = fwdRows[fwdRows.length - 1] ? ratiosAt(fwdRows[fwdRows.length - 1]) : null;

  /* Attach a `series` array of 5 numeric values to each current ratio,
     aligned by ratio name. Renderer plots them as a sparkline. */
  return current.map((ratio, i) => ({
    ...ratio,
    series: [pm3?.[i]?.value, pm1?.[i]?.value, ratio.value, fp1?.[i]?.value, fp3?.[i]?.value],
  }));
}

/* v8 Path L — Ratios as a full horizon table.
   Takes the same (direction, periods, density) signals the 3-statement
   model uses and emits N ratio values per column. Renderer
   (MetricHorizonTable) treats this exactly like a 3-statement table —
   periods as columns, ratios as rows, current-period highlighted.

   Why this exists: user wants every financial metric viewable as a
   data table across time, DRY with the 3-statement framing. This makes
   ratios behave like any other rolling report. */
export function buildFinancialRatiosTimeline(scenario, opts = {}) {
  const direction = opts.direction || getTabDirection(scenario, 'budgets');
  const periods = Math.max(1, opts.periods || normalizePeriodScalar(scenario.formalPeriodCount, 12));
  const density = Math.max(1, opts.density || normalizePeriodScalar(scenario.formalPeriodDensity, 1));
  const totalMonths = periods * density;

  const raw = buildThreeStatementForDirection(scenario, direction, totalMonths, density);
  const rawRows = raw.forecast || [];
  const aggRows = aggregateByDensity(rawRows, density, 'last');
  const periodLabels = aggRows.map(r => fmtPeriod(r.ym ?? r.label, density));
  const currentState = currentPeriodStateForRows(aggRows, density);
  const currentLabel = currentState.label;

  /* Compute ratios at each aggregated period. When the period already
     covers the real "current month" we replace with the live snapshot
     (buildFinancialRatios without override) so snapshot ratios stay
     grounded in actual data rather than synthesized future numbers. */
  const ratiosPerPeriod = aggRows.map((row) => {
    const snap = rowToRatioSnapshot(row, scenario);
    return buildFinancialRatios(scenario, snap);
  });

  /* Transpose — 15 ratios × N periods */
  const firstSet = ratiosPerPeriod[0] || buildFinancialRatios(scenario);
  const rows = firstSet.map((ratio, rIdx) => ({
    ratio: ratio.ratio,
    category: ratio.category,
    format: ratio.format,
    tone: ratio.tone,
    betterWhen: ratio.betterWhen,  /* v8 Path AI — metric polarity */
    direction: ratio.direction,    /* v8 Path AM — direction guide for tooltip */
    formula: ratio.formula,
    hint: ratio.hint,
    explanation: ratio.explanation,
    values: ratiosPerPeriod.map(set => (set[rIdx] ? set[rIdx].value : null)),
  }));

  return { periodLabels, currentLabel, currentState, rows, direction, periods, density };
}

/* ---------- WORKING CAPITAL ---------- */
/* v8 Path AF — DSO kept period-agnostic. AR_RECURRING.avgCollectionDays
   is a model constant (configured receivable streams have fixed
   cadence), so DSO doesn't vary month-to-month in the current data
   model. When we later drive DSO from real atomic-ledger AR aging,
   extend this signature to `calcDSO(scenario, ym)` and thread ym
   through the snapshot builder (mirror of the DPO change below). */
export function calcDSO() {
  return AR_RECURRING.avgCollectionDays;
}

/* v8 Path AF — DPO now accepts a period `ym`. Historically this
   function hardcoded `currentYM()`, so every row in the horizon
   snapshot shared the CURRENT month's arrears-drag. That cascaded
   into identical DPO/CCC/AP-turnover columns across the whole
   12-period table — cosmetically suspicious and analytically
   useless. Callers who want "now" can still pass nothing (default
   preserves back-compat). The per-period snapshot builder now
   passes `row.ym` so each horizon cell reflects that month's AP
   volume + arrears. */
export function calcDPO(scenario, ym = null) {
  const effYM = ym || currentYM();
  const monthlyAP = calcMonthlyFamilyAP(effYM, scenario);
  /* marianArrears() is today-only in the data model — there's no
     historical/projected arrears curve yet. We keep it constant;
     the period-varying signal comes from `monthlyAP` which does
     change month-to-month (the routed support channel has seasonal
     pulses). When arrears become a time series, read them by ym. */
  const arrearsDays = monthlyAP > 0 ? (marianArrears() / monthlyAP) * 30 : 0;
  /* Blended: Net 15 base + arrears drag weighted 30% */
  return Math.round(15 + arrearsDays * 0.3);
}
export function calcCashConversionCycle(scenario, ym = null) {
  return calcDSO() - calcDPO(scenario, ym);
}

/* ---------- MORTGAGES ---------- */
/* v7.2 — when called with a ym + scenario, mortgage schedule.steps win over
   flat monthlyPayment. Keeps old 0-arg callers working. */
export function calcMonthlyMortgages(ym = null, scenario = null) {
  return MORTGAGES.reduce((sum, m) => {
    const schedule = resolveLineSchedule(m, scenario);
    if (schedule && ym) {
      if (schedule.startYM && ym < schedule.startYM) return sum;
      if (schedule.stopYM  && ym >= schedule.stopYM) return sum;
      const steps = Array.isArray(schedule.steps) ? schedule.steps : null;
      if (steps && steps.length > 0) {
        let chosen = null;
        for (const s of steps) {
          if (!s.fromYM || s.fromYM <= ym) chosen = s;
        }
        if (chosen != null) return sum + Number(chosen.amount);
      }
    }
    return sum + m.monthlyPayment;
  }, 0);
}
export function calcMortgageInterest() {
  return MORTGAGES.reduce((s, m) => s + (m.outstanding * m.estRate / 12), 0);
}
export function calcMortgagePrincipal() {
  return calcMonthlyMortgages() - calcMortgageInterest();
}

/* ---------- BALANCES / NET WORTH ---------- */
export function totalAssetsLiquid() {
  const sumBank = BALANCES.bank.reduce((s, a) => s + a.amount, 0);
  const sumCash = BALANCES.cash.reduce((s, a) => s + a.amount, 0);
  return sumBank + sumCash;
}
export function totalCreditCardDebt() {
  return Math.abs(BALANCES.creditCards.filter(c => c.amount < 0).reduce((s, c) => s + c.amount, 0));
}
export function totalCCCredits() {
  return BALANCES.creditCards.filter(c => c.amount > 0).reduce((s, c) => s + c.amount, 0);
}
export function totalLongTermLiabilities() {
  return Math.abs(BALANCES.longTermLiabilities.reduce((s, l) => s + l.amount, 0));
}
export function totalSuspense() {
  return BALANCES.suspense.reduce((s, a) => s + a.amount, 0);
}
export function totalEquity() {
  return BALANCES.equity.reduce((s, e) => s + e.amount, 0);
}
export function netWorth() {
  return totalAssetsLiquid() - totalCreditCardDebt() - totalLongTermLiabilities();
}

/* ============================================================
   v7.3.2 — Balance Sheet sourced from v7.2 data blocks.
   The legacy BALANCES.creditCards + BALANCES.longTermLiabilities
   mix monthly statement activity with outstanding balances and
   miss the fixed-asset side entirely. FIXED_ASSETS, CC_LIABILITIES,
   CASH_BALANCES, and LONG_TERM_LIABILITIES are the v7.2 sources
   of truth for the formal Balance Sheet. Legacy helpers kept for
   backward-compat (KPI cards, sparklines, breakdowns).
   ============================================================ */
/* v8 Path AL — PIECEWISE PINS for fixed-asset valuations and LTL
   outstanding balances.

   Motivation: `totalFixedAssetsNet` and `totalLTLFromBlocks` are called
   at every horizon cell. Without per-ym resolution they return the
   same static number for every period, so the Balance Sheet's fixed
   assets and mortgages payable appear frozen across a 12-month
   forecast. Users can't model "my house will be worth ₱1.6M by
   2026-12" or "my mortgage outstanding will be ₱1.2M by 2027-01".

   Solution: extend the existing piecewise pattern (proven for
   recurring + support AP payment schedules) to POINT-IN-TIME balance
   pins. Shape:

     scenario.piecewiseAssets = {
       'asset-xevera': [
         { ym: '2026-06', value: 1500000, note: 'Mid-year appraisal' },
         { ym: '2026-12', value: 1600000 },
       ],
       ...
     }
     scenario.piecewiseLTLs = {
       'mortgage-xevera': [
         { ym: '2026-06', outstanding: 1280000 },
         { ym: '2027-01', outstanding: 1150000 },
       ],
     }

   Semantics: STEP-HOLD (matches `schedule.steps` resolution elsewhere
   in the engine). For a given ym, the latest pin where `pin.ym <= ym`
   wins. Before the first pin, fall back to the asset's `currentValue`
   (or ltl's `outstanding`). Linear interpolation could come later if
   the user wants smoother trajectories — for now, step-hold keeps
   the mental model consistent with other piecewise features. */
export function resolveAssetValue(asset, ym, scenario) {
  const baseline = asset?.currentValue ?? 0;
  if (!asset?.id || !ym || !scenario?.piecewiseAssets) return baseline;
  const pins = scenario.piecewiseAssets[asset.id];
  if (!Array.isArray(pins) || pins.length === 0) return baseline;
  // Latest pin where pin.ym <= ym wins (step-hold)
  let chosen = null;
  for (const p of pins) {
    if (!p?.ym) continue;
    if (p.ym <= ym && (!chosen || p.ym > chosen.ym)) chosen = p;
  }
  return chosen && typeof chosen.value === 'number' && Number.isFinite(chosen.value)
    ? chosen.value
    : baseline;
}

export function resolveLTLOutstanding(ltl, ym, scenario) {
  const baseline = ltl?.outstanding ?? 0;
  if (!ltl?.id || !ym || !scenario?.piecewiseLTLs) return baseline;
  const pins = scenario.piecewiseLTLs[ltl.id];
  if (!Array.isArray(pins) || pins.length === 0) return baseline;
  let chosen = null;
  for (const p of pins) {
    if (!p?.ym) continue;
    if (p.ym <= ym && (!chosen || p.ym > chosen.ym)) chosen = p;
  }
  return chosen && typeof chosen.outstanding === 'number' && Number.isFinite(chosen.outstanding)
    ? chosen.outstanding
    : baseline;
}

/* v8 Path AL — optional `scenario` + `ym` args. Zero-arg callers
   (KPI cards, sub-section headers, single-period views) still work
   and get today's static totals. Horizon callers (`projectBalanceSheet`,
   `buildFinancialRatios` with a snapshot row) pass ym to get the
   piecewise-resolved value at that period. */
export function totalFixedAssetsNet(scenario = null, ym = null) {
  return FIXED_ASSETS
    .filter(a => a.status !== 'not-yet-acquired')
    .reduce((s, a) => {
      const value = ym && scenario ? resolveAssetValue(a, ym, scenario) : (a.currentValue || 0);
      return s + Math.max(0, value - (a.accumDepreciation || 0));
    }, 0);
}

export function totalCashFromBlocks() {
  return CASH_BALANCES.reduce((s, c) => s + (c.balance || 0), 0);
}

export function totalLiquidCashFromBlocks() {
  return CASH_BALANCES.filter(c => c.liquid).reduce((s, c) => s + (c.balance || 0), 0);
}

export function totalCCDebtFromBlocks() {
  return CC_LIABILITIES.filter(c => c.balance > 0).reduce((s, c) => s + c.balance, 0);
}

export function totalCCCreditsFromBlocks() {
  return CC_LIABILITIES.filter(c => c.balance < 0).reduce((s, c) => s + Math.abs(c.balance), 0);
}

export function totalLTLFromBlocks(scenario = null, ym = null) {
  return LONG_TERM_LIABILITIES.reduce((s, l) => {
    const out = ym && scenario ? resolveLTLOutstanding(l, ym, scenario) : (l.outstanding || 0);
    return s + out;
  }, 0);
}

/* ---------- PIECEWISE LOAN REPAY ---------- */
/* scenario.jmLoanRepaySchedule overrides INCOMES.jmShortLoan.defaultRepaySchedule */
export function getJMRepaySchedule(scenario) {
  return scenario.jmLoanRepaySchedule ?? INCOMES.jmShortLoan.defaultRepaySchedule;
}
export function validateJMRepaySchedule(schedule) {
  const sum = schedule.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const target = INCOMES.jmShortLoan.amount;
  return {
    sum,
    target,
    delta: sum - target,
    valid: Math.abs(sum - target) < 0.005,
  };
}
export function jmRepayInMonth(scenario, ym) {
  const schedule = getJMRepaySchedule(scenario);
  return schedule.reduce((s, r) => {
    const rYM = (r.date || '').slice(0, 7);
    return rYM === ym ? s + Number(r.amount || 0) : s;
  }, 0);
}

/* ---------- RESERVE MILESTONE PROGRESS ---------- */
/* Reserve line — monthly custody contribution, target 2027-01-01 */
export function reserveMilestoneProgress(asOf = currentYM()) {
  const mm01 = RECURRING.monthly.find(r => r.code === 'MM01');
  if (!mm01) return null;
  const startYM = mm01.startYM || '2026-01';
  const targetEvent = mm01.targetEvent || '2027-01-01';
  const targetYM = targetEvent.slice(0, 7);
  const monthsSinceStart = Math.max(0, monthsBetween(startYM, asOf) + 1);
  const monthsTotalPlanned = monthsBetween(startYM, targetYM) + 1;
  const savedToDate = monthsSinceStart * mm01.amount;
  const totalPlanned = monthsTotalPlanned * mm01.amount;
  const pctComplete = Math.min(1, savedToDate / totalPlanned);
  return {
    startYM, targetYM, targetEvent,
    monthsSinceStart, monthsTotalPlanned,
    savedToDate, totalPlanned,
    pctComplete,
    monthsRemaining: Math.max(0, monthsTotalPlanned - monthsSinceStart),
    monthlyAmount: mm01.amount,
    custodian: "Custody reserve",
  };
}

/* ---------- BURN + CASH IN (v5 — inflation, fuel, seasonal applied) ---------- */
export function calcMonthlyBurn(scenario, ym) {
  const effYM = ym || currentYM();
  const subs = calcMonthlyAllRecurring(effYM, scenario);
  const familyDirectAdj = inflatedFamilyDirect(scenario, effYM);
  const marianAdj = inflatedMarian(scenario, effYM);
  const mortgages = calcMonthlyMortgages();
  const payDirect = calcJMPayDirectForMonth(scenario, effYM);
  const jmRepay = jmRepayInMonth(scenario, effYM);
  const fuel = calcMonthlyFuel(scenario, effYM);
  const seasonal = calcSeasonalForMonth(scenario, effYM);
  const lifestyle = calcLifestyleForMonth(scenario, effYM);
  return subs + familyDirectAdj + marianAdj + mortgages + payDirect + jmRepay + fuel + seasonal + lifestyle;
}

export function monthlyBurnBreakdown(scenario, ym) {
  const effYM = ym || currentYM();
  const rows = [
    { label: 'Recurring subs (all tranches)', amount: calcMonthlyAllRecurring(effYM, scenario), source: 'recurring' },
    { label: 'Direct support (CPI + pressure adj.)', amount: inflatedFamilyDirect(scenario, effYM), source: 'FAMILY_AP.direct' },
    { label: 'Routed support channel (CPI + pressure adj.)', amount: inflatedMarian(scenario, effYM), source: 'SUPPORT_AP.routed' },
    { label: 'Mortgage group', amount: calcMonthlyMortgages(), source: 'MORTGAGES' },
    { label: 'Fuel (liters × price × shock × CPI)', amount: calcMonthlyFuel(scenario, effYM), source: 'FUEL' },
    { label: 'Seasonal (ber-months, birthdays, etc.)', amount: calcSeasonalForMonth(scenario, effYM), source: 'SEASONAL' },
    { label: 'Lifestyle', amount: calcLifestyleForMonth(scenario, effYM), source: 'LIFESTYLE' },
    { label: 'Custody stream', amount: calcJMPayDirectForMonth(scenario, effYM), source: 'INCOMES.jmPayDirect' },
    { label: 'Loan repayment', amount: jmRepayInMonth(scenario, effYM), source: 'INCOMES.jmShortLoan' },
  ].filter(r => r.amount > 0).sort((a, b) => b.amount - a.amount);
  return rows;
}

export function calcMonthlyCashIn(scenario, ym) {
  const effYM = ym || currentYM();
  let inflow = 0;
  if (scenario.mtbContinuity) inflow += calcMonthlyNet();
  inflow += calcMonthlyAR(scenario, effYM, { expected: true });
  inflow += calcFVFMRR(scenario, effYM);   // v5 — driver-based
  if (scenario.jmLoanReceived && effYM === (INCOMES.jmShortLoan.expectedDate || '').slice(0, 7)) {
    inflow += INCOMES.jmShortLoan.amount;
  }
  return inflow;
}

export function monthlyCashInBreakdown(scenario, ym) {
  const effYM = ym || currentYM();
  const rows = [];
  if (scenario.mtbContinuity)
    rows.push({ label: 'Primary income (net)', amount: calcMonthlyNet(), source: 'PRIMARY_INCOME' });
  rows.push({ label: 'Contribution AR (expected)', amount: calcExpectedEducationAR(scenario), source: 'AR_RECURRING.educationFund' });
  rows.push({ label: 'Pooled contribution AR', amount: calcMonthlyFamilyPoolAR(effYM), source: 'AR_RECURRING.familyAllowance' });
  if (scenario.fvfLaunchYM && effYM >= scenario.fvfLaunchYM)
    rows.push({ label: `Recurring revenue (${scenario.fvfMethod || 'triangulated'})`, amount: calcFVFMRR(scenario, effYM), source: 'SUBSCRIPTION_DRIVERS' });
  if (scenario.jmLoanReceived && effYM === (INCOMES.jmShortLoan.expectedDate || '').slice(0, 7))
    rows.push({ label: 'Short-term loan (one-time)', amount: INCOMES.jmShortLoan.amount, source: 'INCOMES.jmShortLoan' });
  return rows.filter(r => r.amount !== 0).sort((a, b) => b.amount - a.amount);
}

export function calcFreeCashFlow(scenario, ym) {
  const eff = ym || currentYM();
  return calcMonthlyCashIn(scenario, eff) - calcMonthlyBurn(scenario, eff);
}

export function calcRunway(scenario) {
  let liquid = totalAssetsLiquid();
  let months = 0;
  let ym = currentYM();
  const maxHorizon = 48;
  while (months < maxHorizon) {
    const fcf = calcFreeCashFlow(scenario, ym);
    liquid += fcf;
    if (liquid <= 0) return months + 1;
    months++;
    ym = ymAdd(ym, 1);
  }
  return Infinity;
}

/* ---------- 12-MONTH FORECAST (v5 — inflation, fuel, seasonal, FVF drivers) ---------- */
export function buildForecast(scenario, horizonMonths = 12) {
  const startYM = currentYM();
  const rows = [];
  let liquid = totalAssetsLiquid();
  let ccDebt = totalCreditCardDebt();
  let custodyHeldByJM = 0;
  let fvfCumRevenue = 0;
  let arrears = marianArrears();
  let mortgageOutstanding = MORTGAGES.reduce((s, m) => s + m.outstanding, 0);
  let reserveMilestoneSaved = 0;

  for (let i = 0; i < horizonMonths; i++) {
    const ym = ymAdd(startYM, i);
    const monthNum = parseInt(ym.split('-')[1], 10);
    const salaryNet = scenario.mtbContinuity ? calcMonthlyNet() : 0;

    /* v5 — subscription MRR via driver methodology (top-down / bottom-up / triangulated) */
    const fvfMRR = calcFVFMRR(scenario, ym);
    fvfCumRevenue += fvfMRR;

    /* Loan lump + piecewise repay */
    const jmLoanYM = (INCOMES.jmShortLoan.expectedDate || '').slice(0, 7);
    const jmLumpIn = (scenario.jmLoanReceived && ym === jmLoanYM) ? INCOMES.jmShortLoan.amount : 0;
    const jmRepayOut = jmRepayInMonth(scenario, ym);

    /* AR breakdown (expected, rate-adjusted) */
    const educationAR = calcExpectedEducationAR(scenario);
    const familyPoolAR = calcMonthlyFamilyPoolAR(ym);
    const totalAR = educationAR + familyPoolAR;

    const grossIn = salaryNet + fvfMRR + jmLumpIn + totalAR;

    /* Outflows — v5 inflation/fuel/seasonal overlays */
    const subs = calcMonthlyAllRecurring(ym, scenario);
    const familyDirect = inflatedFamilyDirect(scenario, ym);
    const marianAP = inflatedMarian(scenario, ym);
    const mortgages = calcMonthlyMortgages();
    const yearlyBumps = yearlySubsForMonth(monthNum);
    const fuel = calcMonthlyFuel(scenario, ym);
    const seasonal = calcSeasonalForMonth(scenario, ym);
    const lifestyle = calcLifestyleForMonth(scenario, ym);
    const payDirect = calcJMPayDirectForMonth(scenario, ym);
    custodyHeldByJM += payDirect;

    /* Reserve milestone tracker */
    const mm01 = RECURRING.monthly.find(r => r.code === 'MM01');
    const reserveMilestoneYM = mm01?.targetEvent?.slice(0, 7) ?? '2027-01';
    if (ym <= reserveMilestoneYM && mm01) {
      reserveMilestoneSaved += mm01.amount;
    }

    /* Arrears catch-up heuristic — 15% of surplus */
    let arrearsCatchup = 0;
    const coreOutflows = subs + familyDirect + marianAP + mortgages + yearlyBumps + fuel + seasonal + lifestyle + payDirect + jmRepayOut;
    const provisional = grossIn - coreOutflows;
    if (arrears > 0 && provisional > 0) {
      arrearsCatchup = Math.min(arrears, provisional * 0.15);
      arrears -= arrearsCatchup;
    }

    const baseBurn = coreOutflows + arrearsCatchup;
    const preCCFCF = grossIn - baseBurn;

    /* Short-term debt paydown */
    let ccPaydown = 0;
    if (preCCFCF > 0 && ccDebt > 0) {
      ccPaydown = Math.min(ccDebt, preCCFCF * (scenario.ccPaydownPctOfFreeCash ?? 0.5));
    }
    ccDebt -= ccPaydown;

    const mortgageInterest = MORTGAGES.reduce((s, m) => s + (m.outstanding * m.estRate / 12), 0);
    const mortgagePrincipal = mortgages - mortgageInterest;
    mortgageOutstanding -= mortgagePrincipal;

    const netFCF = preCCFCF - ccPaydown;
    liquid += netFCF;

    rows.push({
      ym, label: ymLabel(ym),
      salaryNet, fvfMRR, jmLumpIn,
      educationAR, familyPoolAR, totalAR,
      subs, familyDirect, marianAP, mortgages, mortgageInterest, mortgagePrincipal,
      yearlyBumps, fuel, seasonal, lifestyle,
      payDirect, jmRepayOut, arrearsCatchup,
      grossIn, baseBurn, preCCFCF, ccPaydown, netFCF,
      liquidEnding: liquid,
      ccDebtEnding: ccDebt,
      marianArrearsEnding: arrears,
      mortgageOutstandingEnding: mortgageOutstanding,
      custodyHeldByJM,
      fvfCumRevenue,
      reserveMilestoneSaved,
      netWorthProxy: liquid - ccDebt + custodyHeldByJM - mortgageOutstanding - arrears,
      /* v5 — inflation factors snapshotted for display */
      cpiFactor: inflationFactor(scenario, ym),
      foodCpiFactor: inflationFactor(scenario, ym, 'food'),
      familyFactor: familyAllowanceFactor(scenario, ym),
    });
  }

  return rows;
}

/* ---------- 3-STATEMENT PROJECTION (v5 — adds fuel/seasonal/lifestyle) ---------- */
export function projectIncomeStatement(row, scenario) {
  const salaryGross = scenario.mtbContinuity ? calcMonthlyGross() : 0;
  const fvfRevenue = row.fvfMRR;
  const familyContribRevenue = row.totalAR;
  const totalRevenue = salaryGross + fvfRevenue + familyContribRevenue;

  const subscriptionsExpense = row.subs;
  const familyExpense = row.familyDirect;
  const marianExpense = row.marianAP;
  const fuelExpense = row.fuel ?? 0;
  const seasonalExpense = row.seasonal ?? 0;
  const lifestyleExpense = row.lifestyle ?? 0;
  const totalOpEx = subscriptionsExpense + familyExpense + marianExpense + fuelExpense + seasonalExpense + lifestyleExpense;

  const mortgageInterest = row.mortgageInterest;
  const wht = scenario.mtbContinuity ? calcMonthlyWHT() : 0;
  const gov = scenario.mtbContinuity ? calcMonthlyGovContribs() : 0;
  const taxesAndGov = wht + gov;

  const totalExpenses = totalOpEx + mortgageInterest + taxesAndGov;
  const netIncome = totalRevenue - totalExpenses;

  return {
    ym: row.ym, label: row.label,
    salaryGross, fvfRevenue, familyContribRevenue, totalRevenue,
    subscriptionsExpense, familyExpense, marianExpense,
    fuelExpense, seasonalExpense, lifestyleExpense,
    totalOpEx,
    mortgageInterest, taxesAndGov, wht, gov,
    totalExpenses, netIncome,
  };
}

export function projectBalanceSheet(row, scenario) {
  const cash = row.liquidEnding;
  const custodyReceivable = row.custodyHeldByJM;
  const accountsReceivable = AR_RECURRING.avgCollectionDays / 30 * row.totalAR;
  /* v8 Path AL — realEstateCost now respects piecewise asset pins
     when the scenario has any configured for this ym. Without pins,
     falls back to the previous MORTGAGES.outstanding sum (proxy for
     house value = what you owe on it, which understates equity but
     preserves pre-AL behavior for un-pinned scenarios). With pins,
     uses totalFixedAssetsNet(scenario, row.ym) so each horizon
     period reflects the user's projected asset-value trajectory. */
  const hasAssetPins = scenario?.piecewiseAssets && Object.keys(scenario.piecewiseAssets).length > 0;
  const realEstateCost = hasAssetPins
    ? totalFixedAssetsNet(scenario, row.ym)
    : MORTGAGES.reduce((s, m) => s + m.outstanding, 0);
  const totalAssets = cash + custodyReceivable + accountsReceivable + realEstateCost;

  const creditCardsPayable = row.ccDebtEnding;
  const accountsPayable = row.marianArrearsEnding + (FAMILY_AP.viaMarian.monthlyAmount / 2);
  /* v8 Path AL — mortgagesPayable honors piecewise LTL pins when set.
     Default path uses `row.mortgageOutstandingEnding` which tracks
     the principal-paydown trajectory engine-side. When the user
     explicitly pins an outstanding balance via piecewiseLTLs, that
     pin wins at its ym (step-held forward to subsequent periods). */
  const hasLTLPins = scenario?.piecewiseLTLs && Object.keys(scenario.piecewiseLTLs).length > 0;
  const mortgagesPayable = hasLTLPins
    ? totalLTLFromBlocks(scenario, row.ym)
    : row.mortgageOutstandingEnding;
  const totalLiabilities = creditCardsPayable + accountsPayable + mortgagesPayable;

  const totalEquity = totalAssets - totalLiabilities;
  return {
    ym: row.ym, label: row.label,
    cash, custodyReceivable, accountsReceivable, realEstateCost, totalAssets,
    creditCardsPayable, accountsPayable, mortgagesPayable, totalLiabilities,
    totalEquity,
  };
}

export function projectCashFlow(row, prevRow, is, scenario) {
  const netIncome = is.netIncome;
  const deltaAR = prevRow ? 0 : 0;
  const deltaAP = row.arrearsCatchup > 0 ? -row.arrearsCatchup : 0;
  const cfo = netIncome + deltaAR + deltaAP;
  const cfi = -row.payDirect;
  const cff = -row.ccPaydown - row.mortgagePrincipal + row.jmLumpIn - row.jmRepayOut;
  const netCashChange = cfo + cfi + cff;
  return {
    ym: row.ym, label: row.label,
    netIncome, deltaAR, deltaAP, cfo,
    custodyOut: -row.payDirect, cfi,
    ccPaydown: -row.ccPaydown, mortgagePrincipal: -row.mortgagePrincipal,
    jmLumpIn: row.jmLumpIn, jmRepayOut: -row.jmRepayOut, cff,
    netCashChange,
  };
}

export function buildThreeStatementProjection(scenario, horizonMonths = 12) {
  const forecast = buildForecast(scenario, horizonMonths);
  const incomeStatements = forecast.map(r => projectIncomeStatement(r, scenario));
  const balanceSheets = forecast.map(r => projectBalanceSheet(r, scenario));
  const cashFlows = forecast.map((r, i) =>
    projectCashFlow(r, i > 0 ? forecast[i - 1] : null, incomeStatements[i], scenario)
  );
  return { forecast, incomeStatements, balanceSheets, cashFlows };
}

/* v8 Path A — Back-cast 3-statement set for CPA (historical/comparative).
   Produces `monthsTotal` rows ending at the current YM (inclusive). Uses
   backCastActuals() for liquid/short-term-debt trajectory and evaluates the same
   recurring schedules (subs, family, marian, mortgages, fuel, seasonal,
   lifestyle) against each historical YM so CPI reversal naturally lowers
   historical amounts. Recurring revenue is 0 (pre-launch), custody transfers and
   loan events are suppressed (handled in forecast only). Rows pass
   through projectIncomeStatement/projectBalanceSheet/projectCashFlow so
   the shape matches buildThreeStatementProjection exactly — safe to swap
   at call sites. `isActual` follows the source row so synthetic fallback
   never looks like strict actual proof. */
export function buildThreeStatementBackcast(scenario, monthsTotal = 12) {
  if (monthsTotal <= 0) {
    return { forecast: [], incomeStatements: [], balanceSheets: [], cashFlows: [] };
  }
  const trajectory = backCastActuals(monthsTotal - 1);  /* returns monthsTotal rows, oldest → snap */
  const mortgageStart = MORTGAGES.reduce((s, m) => s + m.outstanding, 0);
  const arrearsNow = marianArrears();

  const rows = trajectory.map((t) => {
    const ym = t.ym;
    const monthNum = parseInt(ym.split('-')[1], 10);
    const salaryNet = scenario.mtbContinuity ? calcMonthlyNet() : 0;
    const fvfMRR = 0;  /* pre-launch historically */
    const educationAR = calcExpectedEducationAR(scenario);
    const familyPoolAR = calcMonthlyFamilyPoolAR(ym);
    const totalAR = educationAR + familyPoolAR;
    const subs = calcMonthlyAllRecurring(ym, scenario);
    const familyDirect = inflatedFamilyDirect(scenario, ym);
    const marianAP = inflatedMarian(scenario, ym);
    const mortgages = calcMonthlyMortgages();
    const yearlyBumps = yearlySubsForMonth(monthNum);
    const fuel = calcMonthlyFuel(scenario, ym);
    const seasonal = calcSeasonalForMonth(scenario, ym);
    const lifestyle = calcLifestyleForMonth(scenario, ym);
    const mortgageInterest = MORTGAGES.reduce((s, m) => s + (m.outstanding * m.estRate / 12), 0);
    const mortgagePrincipal = mortgages - mortgageInterest;
    const coreOutflows = subs + familyDirect + marianAP + mortgages + yearlyBumps + fuel + seasonal + lifestyle;
    const grossIn = salaryNet + totalAR;
    const baseBurn = coreOutflows;
    const preCCFCF = grossIn - baseBurn;

    return {
      ym,
      label: t.label,
      salaryNet, fvfMRR,
      jmLumpIn: 0,
      educationAR, familyPoolAR, totalAR,
      subs, familyDirect, marianAP, mortgages, mortgageInterest, mortgagePrincipal,
      yearlyBumps, fuel, seasonal, lifestyle,
      payDirect: 0,
      jmRepayOut: 0,
      arrearsCatchup: 0,
      grossIn, baseBurn, preCCFCF,
      ccPaydown: 0,
      netFCF: t.netFCF,
      liquidEnding: t.liquidEnding,
      ccDebtEnding: t.ccDebtEnding,
      marianArrearsEnding: arrearsNow,
      mortgageOutstandingEnding: mortgageStart,
      custodyHeldByJM: 0,
      fvfCumRevenue: 0,
      reserveMilestoneSaved: 0,
      netWorthProxy: t.liquidEnding - t.ccDebtEnding - mortgageStart - arrearsNow,
      cpiFactor: inflationFactor(scenario, ym),
      foodCpiFactor: inflationFactor(scenario, ym, 'food'),
      familyFactor: familyAllowanceFactor(scenario, ym),
      isActual: t.isActual === true,
      source: t.source ?? (t.isActual === true ? 'ledger' : 'synthetic'),
    };
  });

  const incomeStatements = rows.map(r => projectIncomeStatement(r, scenario));
  const balanceSheets = rows.map(r => projectBalanceSheet(r, scenario));
  const cashFlows = rows.map((r, i) =>
    projectCashFlow(r, i > 0 ? rows[i - 1] : null, incomeStatements[i], scenario)
  );
  return { forecast: rows, incomeStatements, balanceSheets, cashFlows };
}

/* v8 Path C — Per-tab window direction. Each tab defaults to a
   discipline-appropriate direction; user overrides live in
   scenario.windowDirection[tabId]. 'present' = spanning (past+future).
   Unknown tabs fall through to 'future'.
   Legacy values 'current' and 'forward' are aliased to 'present' and 'future'
   inside getTabDirection so older stored state degrades gracefully. */
export const WINDOW_DIRECTIONS = ['past', 'present', 'future'];

/* Alias map for backward compatibility: older scenario snapshots may still
   carry 'current' or 'forward'. Read-time only; we never write legacy keys. */
const DIRECTION_ALIAS = { current: 'present', forward: 'future' };
/* v8 Path L — Verbosity levels. Universal user-type setting that lets
   the app reveal more or fewer sub-sections, sparklines, rich tooltips,
   and advanced controls based on user preference. Commercial FVF maps
   to personas: Executive (minimal), Analyst (standard), Power user
   (rich). Every consumer can gate optional richness with
   getVerbosity(scenario) >= 'standard' style checks. */
export const VERBOSITY_LEVELS = ['minimal', 'standard', 'rich'];
export const VERBOSITY_LABELS = {
  minimal:  'Minimal',
  standard: 'Standard',
  rich:     'Rich',
};
export const VERBOSITY_HINTS = {
  minimal:  'Executive view — headline numbers only, sparklines hidden, fewer cards.',
  standard: 'Default — KPIs + sparklines + section cards as shipped.',
  rich:     'Power mode — every ratio, every driver, every explanation shown by default.',
};
export function getVerbosity(scenario) {
  const v = scenario?.verbosity;
  return VERBOSITY_LEVELS.includes(v) ? v : 'standard';
}
export function verbosityAtLeast(scenario, level) {
  const order = { minimal: 0, standard: 1, rich: 2 };
  return order[getVerbosity(scenario)] >= order[level];
}

/* v8 Path P — per-module settings helpers.
   `getModuleFeatures(scenario, moduleId)` returns the merged feature map
   for a module, falling back to defaults (all `true`) when scenario
   state is missing a key. Consumers guard JSX like:
     const features = getModuleFeatures(scenario, 'budgets');
     {features.financialRatios && <RatiosSection/>}
   Defaults live in the App.jsx initial scenario — but this helper adds
   a safety net for older profiles that don't carry moduleSettings yet. */
export const MODULE_FEATURE_DEFAULTS = {
  actuals:   { balanceSheetDetail: true, incomeStatement: true, primaryIncome: true, formalStatements: true },
  budgets:   { financialRatios: true, ratiosTimeline: true, recurringTable: true, familyComposition: true, workingCapital: true, arStreams: true, formalStatements: true },
  forecasts: { drivers: true, overlays: true, comparison: true, scenario: true, formalStatements: true },
  synthesis: { trajectory: true, variance: true, tornado: true, contribution: true, formalStatements: true },
  fcc:       { liquid: true, fixedAssets: true, journalEntries: true, transactions: true, threeStmt: true },
};

export function getModuleFeatures(scenario, moduleId) {
  const defaults = MODULE_FEATURE_DEFAULTS[moduleId] || {};
  const overrides = scenario?.moduleSettings?.[moduleId]?.features || {};
  const normalized = { ...overrides };
  if (moduleId === 'actuals' && normalized.primaryIncome == null && normalized.salary != null) {
    normalized.primaryIncome = normalized.salary;
  }
  return { ...defaults, ...normalized };
}

export function getModulePreferences(scenario, moduleId) {
  return scenario?.moduleSettings?.[moduleId]?.preferences || {};
}

export function setModuleFeature(scenario, moduleId, featureKey, value) {
  const prev = scenario?.moduleSettings?.[moduleId] || {};
  const prevFeatures = prev.features || {};
  return {
    ...scenario,
    moduleSettings: {
      ...(scenario?.moduleSettings || {}),
      [moduleId]: { ...prev, features: { ...prevFeatures, [featureKey]: value } },
    },
  };
}

export function resetModuleSettings(scenario, moduleId) {
  const next = { ...(scenario?.moduleSettings || {}) };
  delete next[moduleId];
  return { ...scenario, moduleSettings: next };
}

export const TAB_DEFAULT_DIRECTION = {
  actuals:   'past',
  budgets:   'present',    /* managerial view wants A/B/F spanning — past ratios + future budget */
  forecasts: 'future',
  synthesis: 'present',
  fcc:       'future',
};
export function getTabDirection(scenario, tabId) {
  const raw = scenario?.windowDirection?.[tabId];
  const override = DIRECTION_ALIAS[raw] ?? raw;   // migrate legacy 'current'/'forward'
  if (WINDOW_DIRECTIONS.includes(override)) return override;
  return TAB_DEFAULT_DIRECTION[tabId] || 'future';
}
/* Dispatches to backcast / combined / projection based on direction.
   For 'present', splits by whole periods (not raw months) so density
   aggregation yields clean period counts on both halves. */
export function buildThreeStatementForDirection(scenario, direction, totalMonths, density = 1) {
  if (direction === 'past')   return buildThreeStatementBackcast(scenario, totalMonths);
  if (direction === 'future') return buildThreeStatementProjection(scenario, totalMonths);
  const d = Math.max(1, density | 0);
  const totalPeriods = Math.max(1, Math.round(totalMonths / d));
  const pastPeriods  = Math.floor(totalPeriods / 2);
  const futurePeriods = Math.ceil(totalPeriods / 2);
  return buildThreeStatementCombined(scenario, pastPeriods * d, futurePeriods * d);
}

/* v8 Path B — Combined 3-statement set for Synthesis (past + forward).
   Concatenates backcast (past, ending at snap inclusive) with projection
   (forward, starting at snap). To avoid duplicating the snapshot month,
   the projection's first row (which equals snap in YM) is dropped — the
   backcast's snap row wins. Returns `pastMonths + futureMonths` rows.
   Both halves share the projectIncomeStatement/Balance/CashFlow shape,
   so the result is a drop-in for aggregateByDensity + shape*Multi. */
export function buildThreeStatementCombined(scenario, pastMonths = 6, futureMonths = 6) {
  const past = buildThreeStatementBackcast(scenario, pastMonths);
  if (futureMonths <= 0) return past;
  const future = buildThreeStatementProjection(scenario, futureMonths + 1);
  /* drop future's first row (duplicate snap); past's snap is the "now" anchor */
  const slice = (arr) => arr.slice(1);
  return {
    forecast:         [...past.forecast,         ...slice(future.forecast)],
    incomeStatements: [...past.incomeStatements, ...slice(future.incomeStatements)],
    balanceSheets:    [...past.balanceSheets,    ...slice(future.balanceSheets)],
    cashFlows:        [...past.cashFlows,        ...slice(future.cashFlows)],
  };
}

/* v7.3.1 — single-month cash-flow snapshot for tabs that don't compute a
   full forecast. Delegates to buildThreeStatementProjection(scenario, 1)
   and returns the first cash-flows row. Shape matches projectCashFlow's
   return so shapeCashFlowStatement accepts it directly. */
export function buildCashFlowSnapshot(scenario, _ym) {
  const { cashFlows } = buildThreeStatementProjection(scenario, 1);
  return cashFlows[0] ?? {
    netIncome: 0, deltaAR: 0, deltaAP: 0, cfo: 0,
    custodyOut: 0, cfi: 0, ccPaydown: 0, mortgagePrincipal: 0,
    jmLumpIn: 0, jmRepayOut: 0, cff: 0, netCashChange: 0,
  };
}

/* ---------- CURRENT-MONTH INCOME STATEMENT ---------- */
export function buildIncomeStatement(scenario) {
  const revenue = scenario.mtbContinuity ? calcMonthlyGross() : 0;
  const fvf = 0;
  const cym = currentYM();
  const subsExpense = calcMonthlyAllRecurring(cym);
  const familyDirect = calcMonthlyFamilyDirect(cym);
  const marianExpense = calcMonthlyMarian(cym);
  const mortgagesInterestPortion = calcMortgageInterest();
  const mortgagesPrincipalPortion = calcMonthlyMortgages() - mortgagesInterestPortion;
  const wht = calcMonthlyWHT();
  const gov = calcMonthlyGovContribs();
  const totalExpenses = subsExpense + familyDirect + marianExpense + mortgagesInterestPortion + wht + gov;
  const netIncome = revenue + fvf - totalExpenses;
  return {
    revenue, fvf,
    subsExpense, familyDirect, marianExpense,
    mortgagesInterestPortion, mortgagesPrincipalPortion,
    wht, gov,
    totalExpenses, netIncome,
  };
}

/* ============================================================
   v5 ENGINES — inflation, fuel, seasonal, lifestyle, FVF drivers
   ============================================================ */

/* ---------- INFLATION ---------- */
/* Years between SNAPSHOT_AS_OF and target YM (fractional) */
export function yearsFromSnapshot(ym) {
  const snapYM = SNAPSHOT_AS_OF.slice(0, 7);
  return monthsBetween(snapYM, ym) / 12;
}

/* Compound CPI factor. Defaults to general CPI; pass 'food' for food CPI. */
export function inflationFactor(scenario, ym, which = 'cpi') {
  const rate = which === 'food'
    ? (scenario.foodCpiAnnualPct ?? INFLATION.foodCpiAnnualPct)
    : (scenario.cpiAnnualPct ?? INFLATION.phCpiAnnualPct);
  const years = yearsFromSnapshot(ym);
  return Math.pow(1 + rate, Math.max(0, years));
}

/* Support allowance pressure — recurring support asks on top of CPI. */
export function familyAllowanceFactor(scenario, ym) {
  const base = inflationFactor(scenario, ym, 'food');  // food-weighted for family
  const pressure = scenario.familyPressurePct ?? INFLATION.familyAllowancePressurePct;
  const years = yearsFromSnapshot(ym);
  return base * Math.pow(1 + pressure, Math.max(0, years));
}

/* ---------- FUEL ---------- */
export function calcMonthlyFuel(scenario, ym) {
  if (!scenario.fuelEnabled) return 0;
  const price   = scenario.fuelPricePerLiter ?? FUEL.phPumpPricePerLiter;
  const liters  = scenario.fuelMonthlyLiters ?? FUEL.monthlyLitersConsumed;
  const shock   = scenario.fuelShockPct      ?? FUEL.geopoliticalShockPct;
  const cpiAdj  = inflationFactor(scenario, ym || currentYM());
  return liters * price * (1 + shock) * cpiAdj;
}

export function fuelBreakdown(scenario, ym) {
  const price   = scenario.fuelPricePerLiter ?? FUEL.phPumpPricePerLiter;
  const liters  = scenario.fuelMonthlyLiters ?? FUEL.monthlyLitersConsumed;
  const shock   = scenario.fuelShockPct      ?? FUEL.geopoliticalShockPct;
  const cpiAdj  = inflationFactor(scenario, ym || currentYM());
  const shockAmt = liters * price * shock * cpiAdj;
  const cpiAmt   = liters * price * (cpiAdj - 1);
  const base     = liters * price;
  return [
    { label: `Base: ${liters} units x ${price}/unit`, amount: base, source: 'baseline' },
    { label: `CPI adjustment (${fmtPct(cpiAdj - 1)})`, amount: cpiAmt, source: 'inflation' },
    { label: `Geopolitical shock (${fmtPct(shock)})`, amount: shockAmt, source: 'shock' },
  ];
}

/* ---------- SEASONAL & LIFESTYLE ---------- */
/* v7 — per-occurrence override resolution (override > multiplier > base).
   scenario.eventOverrides = { [eventId]: { [year]: { multiplier?, customAmount? } } }
   Year key always taken from the YM being resolved (so 2026-11 → year=2026). */
function resolveEventAmountForYear(event, year, eventOverridesMap) {
  const override = eventOverridesMap?.[event.id]?.[String(year)];
  if (override) {
    if (override.customAmount != null) return Number(override.customAmount);
    if (override.multiplier != null)   return Number(event.amount) * Number(override.multiplier);
  }
  return event.amount;
}

/* For a given month number (1-12), sum all SEASONAL events that fire that month.
   Events with month=null are amortized monthly (÷12). */
export function calcSeasonalForMonth(scenario, ym) {
  if (!scenario.seasonalEnabled) return 0;
  const mult = scenario.seasonalMultiplier ?? 1;
  const [year, monthStr] = ym.split('-');
  const monthNum = parseInt(monthStr, 10);
  const yearNum  = parseInt(year, 10);
  const cpiAdj = inflationFactor(scenario, ym, 'food');
  const overrides = scenario.eventOverrides;
  let total = 0;
  for (const e of SEASONAL.events) {
    if (!SEASONAL.categoriesEnabled[e.type]) continue;
    const baseAmount = resolveEventAmountForYear(e, yearNum, overrides);
    if (e.month === null) {
      total += (baseAmount / 12);
    } else if (e.month === monthNum) {
      total += baseAmount;
    }
  }
  return total * mult * cpiAdj;
}

export function calcLifestyleForMonth(scenario, ym) {
  if (!scenario.lifestyleEnabled) return 0;
  const mult = scenario.lifestyleMultiplier ?? 1;
  const [year, monthStr] = ym.split('-');
  const monthNum = parseInt(monthStr, 10);
  const yearNum  = parseInt(year, 10);
  const cpiAdj = inflationFactor(scenario, ym);
  const overrides = scenario.eventOverrides;
  let total = 0;
  for (const e of LIFESTYLE.events) {
    if (!LIFESTYLE.categoriesEnabled[e.type]) continue;
    const baseAmount = resolveEventAmountForYear(e, yearNum, overrides);
    if (e.month === null) {
      total += (baseAmount / 12);
    } else if (e.month === monthNum) {
      total += baseAmount;
    }
  }
  return total * mult * cpiAdj;
}

export function seasonalBreakdown(scenario, ym) {
  const mult = scenario.seasonalMultiplier ?? 1;
  const [year, monthStr] = ym.split('-');
  const monthNum = parseInt(monthStr, 10);
  const yearNum  = parseInt(year, 10);
  const cpiAdj = inflationFactor(scenario, ym, 'food');
  const overrides = scenario.eventOverrides;
  const rows = [];
  for (const e of SEASONAL.events) {
    if (!SEASONAL.categoriesEnabled[e.type]) continue;
    const baseAmount = resolveEventAmountForYear(e, yearNum, overrides);
    const hasOverride = overrides?.[e.id]?.[String(yearNum)];
    const hits = e.month === null ? (baseAmount / 12) : (e.month === monthNum ? baseAmount : 0);
    if (hits > 0) {
      rows.push({
        label: e.label,
        amount: hits * mult * cpiAdj,
        source: e.month === null
          ? `amortized ÷12${hasOverride ? ' · override' : ''}`
          : `M${e.month}${hasOverride ? ' · override' : ''}`,
        cat: e.type,
      });
    }
  }
  return rows.sort((a, b) => b.amount - a.amount);
}

export function lifestyleBreakdown(scenario, ym) {
  const mult = scenario.lifestyleMultiplier ?? 1;
  const [year, monthStr] = ym.split('-');
  const monthNum = parseInt(monthStr, 10);
  const yearNum  = parseInt(year, 10);
  const cpiAdj = inflationFactor(scenario, ym);
  const overrides = scenario.eventOverrides;
  const rows = [];
  for (const e of LIFESTYLE.events) {
    if (!LIFESTYLE.categoriesEnabled[e.type]) continue;
    const baseAmount = resolveEventAmountForYear(e, yearNum, overrides);
    const hasOverride = overrides?.[e.id]?.[String(yearNum)];
    const hits = e.month === null ? (baseAmount / 12) : (e.month === monthNum ? baseAmount : 0);
    if (hits > 0) {
      rows.push({
        label: e.label,
        amount: hits * mult * cpiAdj,
        source: e.month === null
          ? `amortized ÷12${hasOverride ? ' · override' : ''}`
          : `M${e.month}${hasOverride ? ' · override' : ''}`,
        cat: e.type,
      });
    }
  }
  return rows.sort((a, b) => b.amount - a.amount);
}

/* Family allowance with inflation + pressure applied.
   v6 — piecewise wins when present (exact schedule is user-specified and already
   represents what will actually be paid that month); inflation is only applied
   to the default path.
   v7.1 — the routed-channel multiplier scales the routed support default. */
export function inflatedFamilyDirect(scenario, ym) {
  const overrides = scenario?.piecewiseOverrides;
  if (hasResolvedPiecewise(FAMILY_AP.direct, FAMILY_DIRECT_KEY, overrides)) {
    return calcMonthlyFamilyDirect(ym, scenario);
  }
  return calcMonthlyFamilyDirectDefault() * familyAllowanceFactor(scenario, ym);
}
export function inflatedMarian(scenario, ym) {
  const overrides = scenario?.piecewiseOverrides;
  const marianMult = Number(scenario?.multipliers?.marian ?? 1);
  if (hasResolvedPiecewise(FAMILY_AP.viaMarian, MARIAN_KEY, overrides)) {
    return calcMonthlyMarian(ym, scenario) * marianMult;
  }
  return calcMonthlyMarianDefault(ym) * familyAllowanceFactor(scenario, ym) * marianMult;
}

/* ---------- SUBSCRIPTION MRR DRIVERS ---------- */
export function calcFVFMRR_TopDown(scenario, monthsSinceLaunch) {
  const tam = FVF_DRIVERS.topDown.tamTenants;
  const share = scenario.fvfTopDown_marketShare ?? FVF_DRIVERS.topDown.marketSharePctY1;
  const arpu  = scenario.fvfTopDown_arpu        ?? FVF_DRIVERS.topDown.arpuPerMonth;
  /* Ramp share linearly over 12 months, then hold */
  const rampFactor = Math.min(1, (monthsSinceLaunch + 1) / 12);
  const tenants = tam * share * rampFactor;
  return tenants * arpu;
}

export function calcFVFMRR_BottomUp(scenario, monthsSinceLaunch) {
  const initial = scenario.fvfBottomUp_initialTenants ?? FVF_DRIVERS.bottomUp.initialTenants;
  const newPerMo = scenario.fvfBottomUp_newPerMonth  ?? FVF_DRIVERS.bottomUp.monthlyNewTenants;
  const churn    = scenario.fvfBottomUp_churn         ?? FVF_DRIVERS.bottomUp.churnRateMonthly;
  const arpu     = scenario.fvfBottomUp_arpu          ?? FVF_DRIVERS.bottomUp.arpuPerMonth;
  let tenants = initial;
  for (let i = 0; i < monthsSinceLaunch; i++) {
    tenants = tenants * (1 - churn) + newPerMo;
  }
  return tenants * arpu;
}

export function calcFVFMRR_Scenario(scenario, monthsSinceLaunch) {
  const ramp = Math.min(1, (monthsSinceLaunch + 1) / (INCOMES.fvfRevenue.rampMonths || 3));
  return (scenario.fvfTargetMRR ?? 0) * ramp;
}

export function calcFVFMRR(scenario, ym) {
  if (!scenario.fvfLaunchYM || ym < scenario.fvfLaunchYM) return 0;
  const msl = monthsBetween(scenario.fvfLaunchYM, ym);
  const method = scenario.fvfMethod ?? FVF_DRIVERS.defaultMethod;
  if (method === 'top-down')   return calcFVFMRR_TopDown(scenario, msl);
  if (method === 'bottom-up')  return calcFVFMRR_BottomUp(scenario, msl);
  /* triangulated: weighted blend */
  const w = FVF_DRIVERS.triangulated.weights;
  return (
    w.bottomUp    * calcFVFMRR_BottomUp(scenario, msl) +
    w.topDown     * calcFVFMRR_TopDown(scenario, msl)  +
    w.scenarioMRR * calcFVFMRR_Scenario(scenario, msl)
  );
}

export function fvfMRRBreakdown(scenario, ym) {
  if (!scenario.fvfLaunchYM || ym < scenario.fvfLaunchYM) return [];
  const msl = monthsBetween(scenario.fvfLaunchYM, ym);
  const method = scenario.fvfMethod ?? FVF_DRIVERS.defaultMethod;
  if (method === 'top-down') {
    return [{ label: 'Top-down (TAM × share × ARPU)', amount: calcFVFMRR_TopDown(scenario, msl), source: `month ${msl + 1}` }];
  }
  if (method === 'bottom-up') {
    const tenants = (() => {
      const initial = scenario.fvfBottomUp_initialTenants ?? FVF_DRIVERS.bottomUp.initialTenants;
      const newPerMo = scenario.fvfBottomUp_newPerMonth  ?? FVF_DRIVERS.bottomUp.monthlyNewTenants;
      const churn    = scenario.fvfBottomUp_churn         ?? FVF_DRIVERS.bottomUp.churnRateMonthly;
      let t = initial;
      for (let i = 0; i < msl; i++) t = t * (1 - churn) + newPerMo;
      return t;
    })();
    const arpu = scenario.fvfBottomUp_arpu ?? FVF_DRIVERS.bottomUp.arpuPerMonth;
    return [
      { label: `Active tenants (${tenants.toFixed(1)})`, amount: tenants, source: 'count' },
      { label: `x ARPU (${arpu})`, amount: tenants * arpu, source: 'revenue' },
    ];
  }
  const w = FVF_DRIVERS.triangulated.weights;
  const bu = calcFVFMRR_BottomUp(scenario, msl);
  const td = calcFVFMRR_TopDown(scenario, msl);
  const sc = calcFVFMRR_Scenario(scenario, msl);
  return [
    { label: `Bottom-up × ${w.bottomUp}`,    amount: bu * w.bottomUp,    source: 'pipeline' },
    { label: `Top-down × ${w.topDown}`,      amount: td * w.topDown,     source: 'market' },
    { label: `Scenario × ${w.scenarioMRR}`,  amount: sc * w.scenarioMRR, source: 'preset' },
  ];
}

/* ---------- DCF-LITE: FVF business value ---------- */
export function calcFVFValue(scenario, opts = {}) {
  const discountRate   = opts.discountRate   ?? 0.15;
  const growthRate     = opts.growthRate     ?? 0.30;
  const terminalGrowth = opts.terminalGrowth ?? 0.05;
  const years          = opts.years          ?? 5;

  if (!scenario.fvfLaunchYM || !scenario.fvfTargetMRR) {
    return { pv: 0, terminalValue: 0, total: 0 };
  }
  let arr = scenario.fvfTargetMRR * 12;
  let pv = 0;
  for (let y = 1; y <= years; y++) {
    pv += arr / Math.pow(1 + discountRate, y);
    arr *= (1 + growthRate);
  }
  const terminalCF = arr * 0.20;
  const terminalValue = terminalCF / (discountRate - terminalGrowth);
  const pvTerminal = terminalValue / Math.pow(1 + discountRate, years);
  return {
    pv: Math.round(pv),
    terminalValue: Math.round(pvTerminal),
    total: Math.round(pv + pvTerminal),
  };
}

/* ============================================================
   v6 — CROSS-REFERENCE REGISTRY
   Maps a metric key → array of consumer sites where the value flows.
   MoneyCell drill-downs use this to render "Used in: [...]" footers.
   tab keys: 'actuals' | 'budgets' | 'forecasts' | 'settings'
   ============================================================ */
export const REGISTRY = {
  recurringSubs: [
    { tab: 'actuals', section: 'is',         metric: 'Subscriptions Expense' },
    { tab: 'budgets', section: 'recurring',  metric: 'Monthly Recurring Total' },
    { tab: 'forecasts', section: 'forecast',   metric: 'Subs (col)' },
    { tab: 'forecasts', section: 'is',         metric: 'Subscriptions Expense' },
    { tab: 'forecasts', section: 'burn-chart', metric: 'subs stack' },
  ],
  familyDirect: [
    { tab: 'actuals', section: 'is',         metric: 'Support Expense' },
    { tab: 'budgets', section: 'familyAP',   metric: 'Direct Support' },
    { tab: 'forecasts', section: 'forecast',   metric: 'Direct support (col)' },
    { tab: 'forecasts', section: 'burn-chart', metric: 'familyDirect stack' },
  ],
  marianAP: [
    { tab: 'actuals', section: 'is',         metric: 'Routed Support Expense' },
    { tab: 'budgets', section: 'familyAP',   metric: 'Routed Support Channel' },
    { tab: 'budgets', section: 'arrears',    metric: 'Routed Arrears Drag' },
    { tab: 'forecasts', section: 'forecast',   metric: 'Routed Support AP (col)' },
    { tab: 'forecasts', section: 'burn-chart', metric: 'marianAP stack' },
  ],
  fvfMRR: [
    { tab: 'forecasts', section: 'forecast',   metric: 'Recurring revenue (col)' },
    { tab: 'forecasts', section: 'is',         metric: 'Subscription Revenue' },
    { tab: 'forecasts', section: 'kpi',        metric: 'Subscription Method KPI' },
    { tab: 'settings', section: 'drivers', metric: 'Driver Panel' },
  ],
  mortgages: [
    { tab: 'actuals', section: 'kpi',        metric: 'Long-term Liabilities' },
    { tab: 'forecasts', section: 'forecast',   metric: 'Mortgages (col)' },
    { tab: 'forecasts', section: 'is',         metric: 'Mortgage Interest' },
    { tab: 'forecasts', section: 'burn-chart', metric: 'mortgages stack' },
  ],
  fuel: [
    { tab: 'forecasts', section: 'forecast',   metric: 'Fuel (col)' },
    { tab: 'forecasts', section: 'burn-chart', metric: 'fuel stack' },
    { tab: 'settings', section: 'inflation', metric: 'Fuel Panel' },
  ],
  seasonal: [
    { tab: 'forecasts', section: 'forecast',   metric: 'Seasonal (col)' },
    { tab: 'forecasts', section: 'burn-chart', metric: 'seasonal stack' },
    { tab: 'settings', section: 'seasonal', metric: 'Seasonal Editor' },
  ],
  lifestyle: [
    { tab: 'forecasts', section: 'forecast',   metric: 'Lifestyle (col)' },
    { tab: 'forecasts', section: 'burn-chart', metric: 'lifestyle stack' },
  ],
  educationAR: [
    { tab: 'budgets', section: 'ar',         metric: 'Contribution AR' },
    { tab: 'forecasts', section: 'forecast',   metric: 'Cash In (col)' },
  ],
  liquidAssets: [
    { tab: 'actuals', section: 'kpi',        metric: 'Liquid Assets' },
    { tab: 'forecasts', section: 'forecast',   metric: 'Liquid Ending (col)' },
    { tab: 'forecasts', section: 'bs',         metric: 'Cash & Equivalents' },
  ],
  ccDebt: [
    { tab: 'actuals', section: 'kpi',        metric: 'Short-term Debt' },
    { tab: 'forecasts', section: 'forecast',   metric: 'Short-term Debt Ending (col)' },
    { tab: 'forecasts', section: 'bs',         metric: 'Short-term Payable' },
    { tab: 'synthesis', section: 'kpi',  metric: 'Short-term debt actuals/budget/forecast' },
  ],

  /* === v7.1 — extended cross-references === */
  ccCredits: [
    { tab: 'actuals', section: 'kpi',        metric: 'Card Credits' },
  ],
  ltl: [
    { tab: 'actuals', section: 'kpi',        metric: 'Long-term Liabilities' },
    { tab: 'actuals', section: 'liabilities', metric: 'Long-term liability rows' },
  ],
  monthlyMortgages: [
    { tab: 'actuals', section: 'primary-income', metric: 'Monthly obligations KPI' },
    { tab: 'budgets', section: 'arap',       metric: 'Mortgage banks AP' },
    { tab: 'synthesis', section: 'ap',   metric: 'Mortgages AP stream' },
  ],
  netWorth: [
    { tab: 'actuals', section: 'kpi',        metric: 'Net Worth (proxy)' },
    { tab: 'forecasts', section: 'forecast',   metric: 'Net Worth Proxy (col)' },
  ],
  monthlyBurn: [
    { tab: 'forecasts', section: 'kpi',        metric: '12mo cumulative FCF (denom)' },
    { tab: 'forecasts', section: 'burn-chart', metric: 'Burn composition stack' },
    { tab: 'synthesis', section: 'kpi',  metric: 'Net cash flow row' },
  ],
  monthlyCashIn: [
    { tab: 'forecasts', section: 'forecast',   metric: 'Gross In (col)' },
    { tab: 'synthesis', section: 'ar',   metric: 'Unified AR rates' },
  ],
  freeCashFlow: [
    { tab: 'forecasts', section: 'kpi',        metric: '12mo cumulative FCF' },
    { tab: 'synthesis', section: 'kpi',  metric: 'Net cash flow' },
    { tab: 'synthesis', section: 'waterfall', metric: 'Variance walk total' },
  ],
  runwayMonths: [
    { tab: 'forecasts', section: 'kpi',        metric: 'Cash runway gauge' },
  ],
  salaryNet: [
    { tab: 'actuals', section: 'kpi',        metric: 'Monthly Net Primary Income' },
    { tab: 'actuals', section: 'primary-income', metric: 'Primary Income section' },
  ],
  salaryGross: [
    { tab: 'actuals', section: 'primary-income', metric: 'Monthly gross' },
    { tab: 'actuals', section: 'is',         metric: 'Primary income (gross)' },
  ],
  marianArrears: [
    { tab: 'budgets', section: 'arrears',    metric: 'Routed arrears KPI' },
    { tab: 'budgets', section: 'marian-cadence', metric: 'Arrears subtitle' },
  ],
  netEducationBurden: [
    { tab: 'budgets', section: 'kpi',        metric: 'Net Education burden' },
  ],
  familyPoolAR: [
    { tab: 'budgets', section: 'ar',         metric: 'Support pool AR' },
    { tab: 'synthesis', section: 'ar',   metric: 'Family pool stream' },
  ],
  fvfValue: [
    { tab: 'forecasts', section: 'kpi',        metric: 'Subscription DCF KPI' },
  ],
  contributionMargin: [
    { tab: 'synthesis', section: 'cm',   metric: 'Contribution margin panel' },
  ],
  apTotal: [
    { tab: 'synthesis', section: 'ap',   metric: 'Unified AP cadence' },
    { tab: 'budgets', section: 'arap',       metric: 'AR/AP by counterparty' },
  ],
  arTotal: [
    { tab: 'synthesis', section: 'ar',   metric: 'Unified AR rates' },
  ],
};
export function lookupRegistry(key) {
  return REGISTRY[key] || [];
}

/* ============================================================
   v6 — PERIOD COMPARISON ENGINE
   Builds period-over-period comparison rows from a forecast.
   mode: 'mom' | 'qoq' | 'yoy' | 'hybrid' | 'all'
   metricKeys: array of forecast row field names to compare.
   ============================================================ */
export function buildComparison(forecast, mode = 'mom', metricKeys = ['liquidEnding', 'ccDebtEnding', 'netFCF', 'fvfMRR']) {
  if (!Array.isArray(forecast) || forecast.length === 0) return [];
  const lag = (mode === 'mom') ? 1
            : (mode === 'qoq') ? 3
            : (mode === 'yoy') ? 12
            : 1;  // hybrid/all default lag
  const rows = [];
  for (let i = 0; i < forecast.length; i++) {
    const cur = forecast[i];
    const prior = (i - lag >= 0) ? forecast[i - lag] : null;
    const cells = {};
    for (const k of metricKeys) {
      const c = Number(cur[k] ?? 0);
      const p = prior ? Number(prior[k] ?? 0) : null;
      const delta = (p == null) ? null : c - p;
      const pct   = (p == null || p === 0) ? null : delta / Math.abs(p);
      cells[k] = { current: c, prior: p, delta, pct };
    }
    rows.push({ ym: cur.ym, label: cur.label, mode, lag, cells });
  }
  return rows;
}

/* All-time aggregator: total, mean, min, max for each metric across forecast. */
export function buildAllTimeStats(forecast, metricKeys = ['liquidEnding', 'ccDebtEnding', 'netFCF', 'fvfMRR']) {
  const out = {};
  for (const k of metricKeys) {
    const vals = forecast.map(r => Number(r[k] ?? 0)).filter(Number.isFinite);
    if (vals.length === 0) { out[k] = null; continue; }
    const sum = vals.reduce((s, v) => s + v, 0);
    out[k] = {
      total: sum,
      mean: sum / vals.length,
      min: Math.min(...vals),
      max: Math.max(...vals),
      count: vals.length,
      first: vals[0],
      last: vals[vals.length - 1],
    };
  }
  return out;
}

/* ============================================================
   v7 — SYNTHESIS HELPERS
   buildContributionMargin · buildSensitivityTornado · backCastActuals
   ============================================================ */

/* Contribution margin per revenue stream — CMA master's-level read.
   "Direct costs" in operator-finance frame =
   the levies / offsets / channels that exist *only because* the
   revenue stream exists. */
export function buildContributionMargin(scenario, ym = currentYM()) {
  const streams = [];

  /* 1. Primary income - gross pay, taxes/gov are statutory direct costs */
  const salaryGross = scenario.mtbContinuity ? calcMonthlyGross() : 0;
  const wht         = scenario.mtbContinuity ? calcMonthlyWHT()   : 0;
  const gov         = scenario.mtbContinuity ? calcMonthlyGovContribs() : 0;
  const salaryDirect = wht + gov;
  streams.push({
    stream: 'Primary income (gross)',
    gross: salaryGross,
    directCosts: salaryDirect,
    marginAbs: salaryGross - salaryDirect,
    marginPct: salaryGross > 0 ? (salaryGross - salaryDirect) / salaryGross : 0,
    breakdown: [
      { label: 'Withholding tax (BIR)', amount: wht },
      { label: 'Statutory contributions + fees', amount: gov },
    ],
    note: 'Levied at source; never in pocket',
  });

  /* 2. Recurring revenue - direct cost = infra + payment processing (~5% blended) */
  const fvfMRR = calcFVFMRR(scenario, ym);
  const fvfInfraPct = 0.05;
  const fvfDirect = fvfMRR * fvfInfraPct;
  streams.push({
    stream: 'Recurring revenue',
    gross: fvfMRR,
    directCosts: fvfDirect,
    marginAbs: fvfMRR - fvfDirect,
    marginPct: fvfMRR > 0 ? 1 - fvfInfraPct : 0,
    breakdown: [
      { label: `Infra + payment processing (~${(fvfInfraPct * 100).toFixed(0)}%)`, amount: fvfDirect },
    ],
    note: fvfMRR > 0 ? 'Pre-launch margin assumption: 95%' : `Not launched yet (target ${scenario.fvfLaunchYM || '—'})`,
  });

  /* 3. Contribution AR — direct cost = routed channel (the offsetting outflow
     attached to that receivable stream). */
  const eduAR = calcExpectedEducationAR(scenario);
  const marianAP = inflatedMarian(scenario, ym);
  streams.push({
    stream: 'Contribution AR',
    gross: eduAR,
    directCosts: marianAP,
    marginAbs: eduAR - marianAP,
    marginPct: eduAR > 0 ? (eduAR - marianAP) / eduAR : 0,
    breakdown: [
      { label: 'Routed channel outflow (the AP that exists because the AR exists)', amount: marianAP },
    ],
    note: eduAR < marianAP ? 'Negative margin: net education burden' : 'Positive margin: AR exceeds channel cost',
  });

  /* 4. Support pool AR — no direct offsetting cost */
  const famPoolAR = calcMonthlyFamilyPoolAR(ym);
  streams.push({
    stream: 'Support-pool AR',
    gross: famPoolAR,
    directCosts: 0,
    marginAbs: famPoolAR,
    marginPct: 1,
    breakdown: [],
    note: 'No direct offset — pure cash inflow',
  });

  return streams.sort((a, b) => b.marginAbs - a.marginAbs);
}

/* Sensitivity tornado — perturb each driver ±20% and measure 12-month
   cumulative net FCF impact. Returns drivers ranked by absolute impact. */
export function buildSensitivityTornado(scenario, horizon = 12) {
  /* Helper: total netFCF over horizon for a perturbed scenario */
  const totalNetFCF = (sc) => {
    const fc = buildForecast(sc, horizon);
    return fc.reduce((s, r) => s + r.netFCF, 0);
  };

  const baseline = totalNetFCF(scenario);

  /* Perturbation map: driver → {label, perturb(scenario, sign)} */
  const drivers = [
    {
      key: 'fvfTargetMRR',
      label: 'Recurring revenue target',
      hint: 'Subscription revenue ramp ceiling',
      perturb: (sc, mult) => ({ ...sc, fvfTargetMRR: (sc.fvfTargetMRR ?? 0) * mult }),
    },
    {
      key: 'ccPaydownPctOfFreeCash',
      label: 'Debt paydown %',
      hint: 'Aggressive paydown reduces FCF available for liquidity',
      perturb: (sc, mult) => ({
        ...sc,
        ccPaydownPctOfFreeCash: Math.max(0, Math.min(1, (sc.ccPaydownPctOfFreeCash ?? 0.5) * mult)),
      }),
    },
    {
      key: 'multipliers.marian',
      label: 'Routed obligation multiplier',
      hint: 'Direct multiplier on routed support channel — ±25% bracket on the dedicated control',
      perturb: (sc, mult) => ({
        ...sc,
        multipliers: {
          ...(sc.multipliers ?? {}),
          marian: Number((sc.multipliers?.marian ?? 1) * mult),
        },
      }),
    },
    {
      key: 'fuelPricePerLiter',
      label: 'Fuel price',
      hint: 'Geopolitical / pump-price shock',
      perturb: (sc, mult) => ({
        ...sc,
        fuelPricePerLiter: (sc.fuelPricePerLiter ?? 72) * mult,
      }),
    },
    {
      key: 'cpiAnnualPct',
      label: 'CPI inflation',
      hint: 'Headline inflation assumption — compounds across 12mo',
      perturb: (sc, mult) => ({
        ...sc,
        cpiAnnualPct: (sc.cpiAnnualPct ?? 0.04) * mult,
      }),
    },
  ];

  const rows = drivers.map((d) => {
    const upScenario   = d.perturb(scenario, 1.2);
    const downScenario = d.perturb(scenario, 0.8);
    const upFCF   = totalNetFCF(upScenario);
    const downFCF = totalNetFCF(downScenario);
    const plusDelta  = upFCF - baseline;
    const minusDelta = downFCF - baseline;
    const absImpact = Math.max(Math.abs(plusDelta), Math.abs(minusDelta));
    return {
      key: d.key,
      label: d.label,
      hint: d.hint,
      baseline,
      plusDelta,
      minusDelta,
      absImpact,
    };
  });

  return rows.sort((a, b) => b.absImpact - a.absImpact);
}

/* Lazily resolve the ledger-cache reader so engine.js has no static
   import edge to data-ledger.js (avoids cycles). `globalThis.__lensLedger`
   is populated by the setter in ledger-cache.js which is a tiny leaf
   module — this is belt-and-suspenders in case the module-graph lookup
   returns undefined during SSR or test harnesses. */
import { getMonthlyLedgerCache as _ledgerCacheReaderImpl } from './ledger-cache.js';
function _ledgerCacheReader() {
  try { return _ledgerCacheReaderImpl(); } catch { return null; }
}

/* Back-cast `monthsBack+1` months of historical actuals (oldest → current
   inclusive). In order of preference:
     1. Real ledger rollups (public/ledger.json OR imported ledger snapshots via
        data-ledger.js cache). Populated by App.jsx on mount.
     2. Synthetic drift (±2%/mo). Fallback when no real data.
   The monthly ledger cache is read synchronously; the app's startup
   preload (loadMonthlyLedger) is what makes this branch available.
   Returns {ym, label, liquidEnding, ccDebtEnding, netFCF, fvfMRR, isActual}. */
export function backCastActuals(monthsBack = 3) {
  const anchorYM = currentYM();
  /* v8 Path G — read the ledger cache via a dedicated module.
     Replaces the window/globalThis bridge. data-ledger.js populates it
     once the async load resolves; before that it's null → synthetic. */
  const cached = _ledgerCacheReader();
  if (Array.isArray(cached) && cached.length > 0) {
    /* Filter to the trailing `monthsBack+1` months ending at the current
       cache may cover more or less; we slice generously and return what
       we have. If cache doesn't reach current month, the last entries
       re-assert the live balance so the period boundary stays current. */
    const wanted = [];
    for (let i = monthsBack; i >= 0; i--) wanted.push(ymAdd(anchorYM, -i));
    const byYM = {};
    for (const r of cached) byYM[r.ym] = r;
    const out = wanted.map((ym) => {
      const r = byYM[ym];
      if (r) {
        return {
          ym,
          label: ymLabel(ym),
          liquidEnding: r.liquidEnding ?? 0,
          ccDebtEnding: r.ccDebtEnding ?? 0,
          netFCF: r.netFCF ?? 0,
          fvfMRR: r.fvfRevenue ?? 0,
          isActual: true,
          source: 'ledger',
        };
      }
      /* No cached entry for this month — fall through to synthetic math
         so gaps don't blow up downstream charts. */
      const liquidNow = totalAssetsLiquid();
      const ccNow = totalCreditCardDebt();
      const i = wanted.length - 1 - wanted.indexOf(ym);  /* months from current */
      return {
        ym,
        label: ymLabel(ym),
        liquidEnding: liquidNow / Math.pow(1.02, i),
        ccDebtEnding: ccNow * Math.pow(1.02, i),
        netFCF: liquidNow * 0.02,
        fvfMRR: 0,
        isActual: false,   /* marked synthetic for UI badging */
        source: 'synthetic-gap',
      };
    });
    return out;
  }
  /* No ledger cache — full synthetic path (original v1 behaviour). */
  const liquidNow = totalAssetsLiquid();
  const ccNow = totalCreditCardDebt();
  const liquidGrowth = 0.02;
  const ccDecay      = 0.02;
  const out = [];
  for (let i = monthsBack; i >= 1; i--) {
    const ym = ymAdd(anchorYM, -i);
    out.push({
      ym,
      label: ymLabel(ym),
      liquidEnding: liquidNow / Math.pow(1 + liquidGrowth, i),
      ccDebtEnding: ccNow * Math.pow(1 + ccDecay, i),
      netFCF: liquidNow * liquidGrowth,
      fvfMRR: 0,
      isActual: false,
      source: 'synthetic',
    });
  }
  out.push({
    ym: anchorYM,
    label: ymLabel(anchorYM),
    liquidEnding: liquidNow,
    ccDebtEnding: ccNow,
    netFCF: liquidNow * liquidGrowth,
    fvfMRR: 0,
    isActual: false,
    source: 'synthetic',
  });
  return out;
}

/* Sparkline series for a KPI metric — 3mo back-cast + 12mo forward.
   Returns [{ ym, value }] suitable for KPISparkline.
   Supported metrics: 'liquid' | 'ccDebt' | 'netFCF' | 'fvfMRR' | 'familyAP' | 'recurring' */
export function kpiSparkSeries(metric, scenario, opts = {}) {
  const back = opts.backMonths ?? 3;
  const fwd  = opts.forwardMonths ?? 12;
  const actuals = backCastActuals(back).slice(0, -1);  /* drop snap (forecast covers it) */
  const forecast = buildForecast(scenario, fwd);
  const all = [...actuals, ...forecast];

  const get = (r) => {
    switch (metric) {
      case 'liquid':    return r.liquidEnding;
      case 'ccDebt':    return r.ccDebtEnding;
      case 'netFCF':    return r.netFCF;
      case 'fvfMRR':    return r.fvfMRR ?? 0;
      case 'familyAP':  return (r.familyDirect ?? 0) + (r.marianAP ?? 0);
      case 'recurring': return r.subs ?? 0;
      default:          return 0;
    }
  };

  return all.map(r => ({ ym: r.ym, label: r.label, value: get(r) }));
}

/* Combined trajectory: actuals back-cast + forward forecast.
   Used by SYNTHESIS trajectory chart. The snap month appears in both
   functions (back-cast ends at it; forecast starts at it). We drop it
   from the back-cast so the forecast's "current month projected" wins
   at the transition. */
export function buildCombinedTrajectory(scenario, horizonMonths = 12, backMonths = 3) {
  const actuals = backCastActuals(backMonths);
  const forecast = buildForecast(scenario, horizonMonths);
  return [
    ...actuals.slice(0, -1).map(r => ({ ...r, segment: 'actual' })),
    ...forecast.map(r => ({ ...r, segment: 'forecast', isActual: false })),
  ];
}

/* ============================================================
   v8 Path N — Central Simulations Registry
   ============================================================
   Every user-authored "what-if" lives inside `scenario`. It's spread
   across many fields: assetTransactions (FND), adjustingEntries (CPA),
   piecewiseOverrides (CMA), multipliers (CFA), fxRates, accountVisibility,
   windowDirection, jmLoanRepaySchedule, jpMode, includeAdjustments…

   `buildSimulationsRegistry(scenario)` walks the whole scenario and
   returns a flat, read-only list of `{ id, module, kind, label, detail,
   impact, editRoute, reason }` entries — ready to render as a single
   audit ledger in FND's "Simulated transactions" sub-view.

   This is the discipline side of the taxonomy (P-simulation-taxonomy.md):
   each simulation stays *authored* in its owning module (CPA / CMA / CFA /
   FND / Settings) but is *surfaced centrally* here so the user can see
   every override they've stacked without tab-hopping. Edits always bounce
   back to the owning module via `editRoute` — FND never mutates them.

   Entries are ordered by module then insertion — stable so the registry
   doesn't flicker when re-memoized. */
export function buildSimulationsRegistry(scenario) {
  if (!scenario) return [];
  const entries = [];

  /* ----- FND · Asset transactions (buy / sell / ltl-payoff / ltl-new) ----- */
  const txns = scenario.assetTransactions ?? [];
  for (const t of txns) {
    const modeLabel =
      t.mode === 'sell'        ? 'Asset SELL'
      : t.mode === 'buy'         ? 'Asset BUY'
      : t.mode === 'ltl-payoff'  ? 'LTL PAYOFF'
      : t.mode === 'ltl-new'     ? 'NEW LTL'
      : String(t.mode || 'TXN').toUpperCase();
    const subject = t.assetName || t.ltlName || t.name || t.assetId || '—';
    const when = t.closingDate || t.payoffDate || t.startDate || '';
    const netCash = Number.isFinite(t.netCash) ? t.netCash : 0;
    entries.push({
      id: `fcc-txn-${t.id}`,
      module: 'FCC',
      kind: 'asset-txn',
      label: `${modeLabel} · ${subject}`,
      detail: when ? `Dated ${when}` : 'Undated',
      impact: `Net cash ${fmtMoney(netCash, { decimals: 0 })}`,
      editRoute: 'fcc:nc-assets-liabilities',
      reason: 'Model-state NC-Asset or NC-Liability event',
    });
  }

  /* ----- FCC · Posted adjusting entries (moved from Actuals 2026-04-25
     per Proposal 3 — JEs are simulations and live alongside buy/sell
     and LTL sims in the FCC sandbox). ----- */
  const jes = scenario.adjustingEntries ?? [];
  for (const e of jes.filter(x => x?.status === 'posted')) {
    const lines = Array.isArray(e.lines) ? e.lines : [];
    const net = lines.reduce((s, l) => s + ((Number(l.debit) || 0) - (Number(l.credit) || 0)), 0);
    const affects = (e.affects ?? []).join('+').toUpperCase() || '—';
    entries.push({
      id: `fcc-je-${e.id}`,
      module: 'FCC',
      kind: 'journal-entry',
      label: `JE · ${e.memo || '(no memo)'}`,
      detail: `${e.date || '—'} · ${affects}`,
      impact: `Net ${fmtMoney(net, { decimals: 0 })}`,
      editRoute: 'fcc:journal-entries',
      reason: 'Posted adjusting entry — stacks onto Income Statement / Balance Sheet per `affects`',
    });
  }

  /* ----- CMA · Piecewise recurring overrides (per-line + support channels) ----- */
  const po = scenario.piecewiseOverrides ?? {};
  for (const key of Object.keys(po)) {
    const sched = po[key] || {};
    const monthCount = Object.keys(sched).length;
    if (monthCount === 0) continue;
    if (key === FAMILY_DIRECT_KEY) {
      entries.push({
        id: 'budgets-piecewise-family-direct',
        module: 'Budgets',
        kind: 'family-direct-override',
        label: 'Direct support · piecewise schedule',
        detail: `${monthCount} month${monthCount === 1 ? '' : 's'} pinned`,
        impact: '—',
        editRoute: 'drivers:piecewise',
        reason: 'Overridden via Drivers -> Model schedules (operating obligations)',
      });
    } else if (key === MARIAN_KEY) {
      entries.push({
        id: 'budgets-piecewise-marian',
        module: 'Budgets',
        kind: 'marian-override',
        label: 'Routed support channel · piecewise schedule',
        detail: `${monthCount} month${monthCount === 1 ? '' : 's'} pinned`,
        impact: '—',
        editRoute: 'drivers:piecewise',
        reason: 'Overridden via Drivers -> Model schedules (routed obligation channel)',
      });
    } else {
      entries.push({
        id: `budgets-piecewise-${key}`,
        module: 'Budgets',
        kind: 'recurring-override',
        label: `Recurring override · ${key}`,
        detail: `${monthCount} month${monthCount === 1 ? '' : 's'} pinned`,
        impact: '—',
        editRoute: 'drivers:recurring',
        reason: 'Overridden via Drivers -> Recurring Schedules (piecewise)',
      });
    }
  }

  /* ----- CMA · Contributor receivable mode + custom contributor amounts ----- */
  if (scenario.jpMode && scenario.jpMode !== 'current') {
    const detail = scenario.jpMode === 'custom'
      ? `Custom: ${fmtMoney(scenario.jpAmount ?? 0, { decimals: 0 })}`
      : 'Historical reference applied';
    entries.push({
      id: 'budgets-jp-mode',
      module: 'Budgets',
      kind: 'sibling-ar',
      label: `Contributor A · ${scenario.jpMode} mode`,
      detail,
      impact: '—',
      editRoute: 'settings:model-assumptions',
      reason: 'Contributor receivable mode set to non-default',
    });
  }
  const basePreset = PRESETS?.base || {};
  if (scenario.jcAmount != null && scenario.jcAmount !== basePreset.jcAmount) {
    entries.push({
      id: 'budgets-jc-amount',
      module: 'Budgets',
      kind: 'sibling-ar',
      label: 'Contributor B · custom amount',
      detail: `${fmtMoney(scenario.jcAmount, { decimals: 0 })} (base: ${fmtMoney(basePreset.jcAmount ?? 0, { decimals: 0 })})`,
      impact: '—',
      editRoute: 'settings:model-assumptions',
      reason: 'Contributor B receivable amount tuned away from base preset',
    });
  }
  if (scenario.jmAmount != null && scenario.jmAmount !== basePreset.jmAmount) {
    entries.push({
      id: 'budgets-jm-amount',
      module: 'Budgets',
      kind: 'sibling-ar',
      label: 'Contributor C · custom amount',
      detail: `${fmtMoney(scenario.jmAmount, { decimals: 0 })} (base: ${fmtMoney(basePreset.jmAmount ?? 0, { decimals: 0 })})`,
      impact: '—',
      editRoute: 'settings:model-assumptions',
      reason: 'Contributor C AR amount tuned away from base preset',
    });
  }

  /* ----- CFA · Multipliers (any value ≠ 1.0) ----- */
  const mult = scenario.multipliers ?? {};
  for (const k of Object.keys(mult)) {
    const v = mult[k];
    if (v == null || Math.abs(v - 1) < 1e-9) continue;
    entries.push({
      id: `forecasts-mult-${k}`,
      module: 'Forecasts',
      kind: 'multiplier',
      label: `Multiplier · ${k}`,
      detail: `×${Number(v).toFixed(2)} (default 1.00)`,
      impact: v > 1 ? 'Scaled UP' : 'Scaled DOWN',
      editRoute: 'settings:model-assumptions',
      reason: 'Tuned via Model assumptions -> Multipliers',
    });
  }

  /* ----- CFA · subscription launch / target (only if different from base preset) ----- */
  if (scenario.fvfLaunchYM && basePreset.fvfLaunchYM && scenario.fvfLaunchYM !== basePreset.fvfLaunchYM) {
    entries.push({
      id: 'forecasts-fvf-launch',
      module: 'Forecasts',
      kind: 'fvf-driver',
      label: 'Subscription launch date override',
      detail: `${scenario.fvfLaunchYM} (base: ${basePreset.fvfLaunchYM})`,
      impact: '—',
      editRoute: 'drivers:model-drivers',
      reason: 'Subscription launch month tuned away from base preset',
    });
  }
  if (scenario.fvfTargetMRR != null && basePreset.fvfTargetMRR != null && scenario.fvfTargetMRR !== basePreset.fvfTargetMRR) {
    entries.push({
      id: 'forecasts-fvf-mrr',
      module: 'Forecasts',
      kind: 'fvf-driver',
      label: 'Recurring revenue target override',
      detail: `${fmtMoney(scenario.fvfTargetMRR, { decimals: 0 })} (base: ${fmtMoney(basePreset.fvfTargetMRR, { decimals: 0 })})`,
      impact: '—',
      editRoute: 'drivers:model-drivers',
      reason: 'Recurring revenue target tuned away from base preset',
    });
  }

  /* ----- CFA · custom repay schedule ----- */
  if (scenario.jmLoanRepaySchedule) {
    entries.push({
      id: 'forecasts-jm-repay',
      module: 'Forecasts',
      kind: 'jm-repay',
      label: 'Loan · custom repay schedule',
      detail: 'Schedule overridden',
      impact: '—',
      editRoute: 'settings:model-assumptions',
      reason: 'Overridden via Model assumptions -> financing repayment schedule',
    });
  }

  /* ----- Settings · FX rate pins ----- */
  const fx = scenario.fxRates ?? {};
  for (const ccy of Object.keys(fx)) {
    if (fx[ccy] == null) continue;
    entries.push({
      id: `settings-fx-${ccy}`,
      module: 'Settings',
      kind: 'fx-override',
      label: `FX pin · ${ccy}`,
      detail: `Locked at ${fx[ccy]}`,
      impact: '—',
      editRoute: 'settings:model-assumptions',
      reason: 'Pinned via Model assumptions -> FX rates',
    });
  }

  /* ----- Settings · Account visibility ----- */
  const vis = scenario.accountVisibility ?? {};
  const visCount = Object.keys(vis).length;
  if (visCount > 0) {
    entries.push({
      id: 'settings-account-visibility',
      module: 'Settings',
      kind: 'account-visibility',
      label: 'Account visibility customized',
      detail: `${visCount} account${visCount === 1 ? '' : 's'} toggled`,
      impact: '—',
      editRoute: 'settings:visibility',
      reason: 'Per-account visibility customized in Settings → Account Visibility',
    });
  }

  /* ----- Settings · Include-JEs toggle OFF (non-default) ----- */
  if (scenario.includeAdjustments === false) {
    entries.push({
      id: 'settings-exclude-je',
      module: 'Settings',
      kind: 'je-toggle',
      label: 'Adjusting Entries excluded',
      detail: '"Include JEs" toggle is OFF — JEs hidden from totals',
      impact: '—',
      editRoute: 'settings:model-assumptions',
      reason: 'Global Include JEs switch toggled off (non-default)',
    });
  }

  /* ----- Settings · Per-tab window direction overrides ----- */
  const wd = scenario.windowDirection ?? {};
  for (const tab of Object.keys(wd)) {
    const def = TAB_DEFAULT_DIRECTION[tab];
    if (!def) continue;
    if (wd[tab] && wd[tab] !== def) {
      entries.push({
        id: `settings-wd-${tab}`,
        module: 'Settings',
        kind: 'window-direction',
        label: `${tab.toUpperCase()} window direction override`,
        detail: `${wd[tab]} (discipline default: ${def})`,
        impact: '—',
        editRoute: `${tab}:overview`,
        reason: 'Discipline default direction overridden via tab picker',
      });
    }
  }

  return entries;
}

/* Aggregate counts per module — tiny helper for registry header summary. */
export function summarizeSimulationsRegistry(entries) {
  const byModule = {};
  for (const e of entries) {
    byModule[e.module] = (byModule[e.module] || 0) + 1;
  }
  return { total: entries.length, byModule };
}

/* ============================================================
   v8 Path Q — Comparative Tables (module-level KPI timelines)
   ============================================================
   Every module gets a universal "Comparative Tables" sub-view. It flips
   the module's chart/KPI/ratio content into `<MetricHorizonTable/>`
   format: N metrics × M periods × Direction, respecting global
   ScenarioBar scalars (formalPeriodCount × formalPeriodDensity ×
   tab direction).

   `MODULE_KPI_REGISTRY[moduleId]` is the catalogue of metrics for each
   module. Each entry is `{ key, label, category, format, tone, extract }`
   where `extract(row, { is, bs, cf })` returns the value for one period.
   Adding a new KPI to a module's comparative view is one registry entry.

   `buildKPIsTimeline(scenario, moduleId, opts)` walks each aggregated
   period and extracts values. Rows conform to MetricHorizonTable's shape
   (ratio/category/format/tone/values) so the renderer is already DRY.

   This is the "data tables for every metric" ship the user requested —
   no more "chart-only" insights, every number is also viewable as a
   rolling horizon table. */
/* v8 Path AH — normalized IS/BS/CF accessors.
   Two generations of field names coexist in the codebase:
     OLD (single-period shapes, FND snapshot paths):
        is: {revenue, fvf, subsExpense, familyDirect, mortgagesInterestPortion}
        bs: {ccDebt, ccCredits, ltl, equity, fixedAssetsNet}
        cf: {netChangeCash}
     NEW (projectIncomeStatement / projectBalanceSheet / projectCashFlow):
        is: {salaryGross, fvfRevenue, familyContribRevenue, totalRevenue,
             subscriptionsExpense, familyExpense, marianExpense,
             mortgageInterest, totalExpenses, netIncome}
        bs: {cash, accountsReceivable, realEstateCost, totalAssets,
             creditCardsPayable, accountsPayable, mortgagesPayable,
             totalLiabilities, totalEquity}
        cf: {netIncome, cfo, cfi, cff, netCashChange}

   Multi-period horizon tables route through the NEW shapes, but the
   KPI registry below was written for OLD. Every extractor previously
   returned undefined → `|| 0` → fmtAmount(0) → '—' (em-dash),
   making CPA / CMA / CFA / FND KPI tables look broken. These small
   getters bridge the two worlds; registry consumers read through
   them and get whichever field exists (NEW wins when both exist so
   we move toward the canonical shape without a flag day). */
const IS_GET = {
  salary:      (is) => is?.salaryGross ?? is?.revenue ?? 0,
  fvf:         (is) => is?.fvfRevenue ?? is?.fvf ?? 0,
  familyAR:    (is) => is?.familyContribRevenue ?? 0,
  totalRev:    (is) => is?.totalRevenue ?? ((is?.salaryGross ?? is?.revenue ?? 0) + (is?.fvfRevenue ?? is?.fvf ?? 0) + (is?.familyContribRevenue ?? 0)),
  subs:        (is) => is?.subscriptionsExpense ?? is?.subsExpense ?? 0,
  familyExp:   (is) => is?.familyExpense ?? is?.familyDirect ?? 0,
  marianExp:   (is) => is?.marianExpense ?? 0,
  mortgageInt: (is) => is?.mortgageInterest ?? is?.mortgagesInterestPortion ?? 0,
  wht:         (is) => is?.wht ?? 0,
  gov:         (is) => is?.gov ?? 0,
  netIncome:   (is) => is?.netIncome ?? 0,
  totalExp:    (is) => is?.totalExpenses ?? 0,
};
const BS_GET = {
  ccDebt:      (bs) => bs?.creditCardsPayable ?? bs?.ccDebt ?? 0,
  ccCredits:   (bs) => bs?.ccCredits ?? 0,           /* not in NEW shape; see note below */
  ltl:         (bs) => bs?.mortgagesPayable ?? bs?.ltl ?? 0,
  equity:      (bs) => bs?.totalEquity ?? bs?.equity ?? 0,
  fixedAssets: (bs) => bs?.realEstateCost ?? bs?.fixedAssetsNet ?? 0,
};
const CF_GET = {
  cfo:           (cf) => cf?.cfo ?? 0,
  netChangeCash: (cf) => cf?.netCashChange ?? cf?.netChangeCash ?? 0,
};

/* Short-term credits note: the NEW multi-period BS shape doesn't track
   credits separately — `row.ccDebtEnding` is a single net number
   (debt − credits). To avoid em-dashing the FND row, we fall back
   to the scenario-level `totalCCCredits()` constant, which is the
   closest approximation (current short-term credit balance held steady
   across the horizon). Keeping as a TODO for when the forecast
   engine tracks short-term credits per-period. */

/* ============================================================
   v8 Path AN — Module data-inheritance (Proposal 1, 2026-04-25)
   ============================================================
   Causality pattern: a tab can render upstream modules' KPIs as a
   "context" panel at the bottom of its overview. The list below
   declares which upstream modules each tab inherits FROM.

   Reasoning per module:
     - actuals    — origin tab, no upstream
     - budgets    — present needs past for variance baselining
     - forecasts  — future needs both present (baseline) + past (trend)
     - synthesis  — composed via Proposal 2 (no fixed inheritance)
     - fcc        — sandbox; needs ledger baseline to know what it
                    is mutating

   Order matters: ContextPanel renders upstream modules in the order
   listed (closest causal neighbour first). Forecasts shows Budgets
   above Actuals so users see the immediate baseline before the
   historical trend. */
export const MODULE_CONTEXT_FROM = Object.freeze({
  actuals:   [],
  budgets:   ['actuals'],
  forecasts: ['budgets', 'actuals'],
  synthesis: [],
  fcc:       ['actuals'],
});

export const MODULE_KPI_REGISTRY = {
  actuals: [
    { key: 'revenue',       label: 'Revenue (primary income + recurring)', category: 'Income Statement', format: 'currency', tone: 'good', betterWhen: 'higher', extract: (_r, { is }) => IS_GET.salary(is) + IS_GET.fvf(is) },
    { key: 'subsExpense',   label: 'Subscriptions expense',  category: 'Income Statement', format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (_r, { is }) => IS_GET.subs(is) },
    { key: 'familyDirect',  label: 'Support AP expense',     category: 'Income Statement', format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (_r, { is }) => IS_GET.familyExp(is) + IS_GET.marianExp(is) },
    { key: 'wht',           label: 'WHT + gov',              category: 'Income Statement', format: 'currency', tone: 'bad',     betterWhen: 'neutral',extract: (_r, { is }) => IS_GET.wht(is) + IS_GET.gov(is) },
    { key: 'netIncome',     label: 'Net Income',             category: 'Income Statement', format: 'currency', tone: 'neutral', betterWhen: 'higher', extract: (_r, { is }) => IS_GET.netIncome(is) },
    { key: 'liquidEnding',  label: 'Liquid ending',          category: 'Balance Sheet',    format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (r) => r?.liquidEnding },
    { key: 'ccDebt',        label: 'Short-term debt',        category: 'Balance Sheet',    format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (_r, { bs }) => BS_GET.ccDebt(bs) },
    { key: 'ltl',           label: 'Long-term liabilities',  category: 'Balance Sheet',    format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (_r, { bs }) => BS_GET.ltl(bs) },
    { key: 'equity',        label: 'Equity',                 category: 'Balance Sheet',    format: 'currency', tone: 'neutral', betterWhen: 'higher', extract: (_r, { bs }) => BS_GET.equity(bs) },
    { key: 'cfo',           label: 'CFO (operating)',        category: 'Cash Flow',        format: 'currency', tone: 'neutral', betterWhen: 'higher', extract: (_r, { cf }) => CF_GET.cfo(cf) },
    { key: 'netChangeCash', label: 'Net change in cash',     category: 'Cash Flow',        format: 'currency', tone: 'neutral', betterWhen: 'higher', extract: (_r, { cf }) => CF_GET.netChangeCash(cf) },
  ],

  budgets: [
    { key: 'recurring',     label: 'Recurring subs (all)',   category: 'Working Capital',  format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (_r, { is }) => IS_GET.subs(is) },
    { key: 'familyAP',      label: 'Support AP total',       category: 'Working Capital',  format: 'currency', tone: 'warn',    betterWhen: 'neutral',extract: (_r, { is }) => IS_GET.familyExp(is) + IS_GET.marianExp(is) },
    { key: 'mortgageInt',   label: 'Mortgage interest',      category: 'Working Capital',  format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (_r, { is }) => IS_GET.mortgageInt(is) },
    { key: 'fvfMRR',        label: 'Recurring revenue',      category: 'Revenue Drivers',  format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (r) => r?.fvfMRR || 0 },
    /* Primary income net — prefer row-level `salaryNet` (post-deduction);
       fall back to IS `salaryGross` (pre-deduction) which is the only
       primary-income field on the IS shape. Not identical but never em-dash. */
    { key: 'salaryNet',     label: 'Primary income net',     category: 'Revenue Drivers',  format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (r, { is }) => r?.salaryNet ?? IS_GET.salary(is) },
    { key: 'netIncome',     label: 'Net Income',             category: 'Profitability',    format: 'currency', tone: 'neutral', betterWhen: 'higher', extract: (_r, { is }) => IS_GET.netIncome(is) },
    { key: 'grossMargin',   label: 'Gross margin %',         category: 'Profitability',    format: 'pct',      tone: 'neutral', betterWhen: 'higher', extract: (_r, { is }) => {
      const rev = IS_GET.salary(is) + IS_GET.fvf(is);
      return rev > 0 ? IS_GET.netIncome(is) / rev : null;
    } },
    { key: 'liquidEnding',  label: 'Liquid ending',          category: 'Liquidity',        format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (r) => r?.liquidEnding },
    /* Runway (months) — v8 Path AH.1 matches Financial Ratios
       semantics: `liquid ÷ total monthly expenses` (income-stops).
       Previously returned null on profitable months, which rendered
       em-dash; now every period gets a real number. When totalExp is
       somehow zero, returns Infinity → fmtMetric renders "∞mo". */
    { key: 'runwayMonths',  label: 'Runway (months)',        category: 'Liquidity',        format: 'months',   tone: 'good',    betterWhen: 'higher', extract: (r, { is }) => {
      const liquid = r?.liquidEnding ?? 0;
      const totalExp = IS_GET.totalExp(is) || (IS_GET.subs(is) + IS_GET.familyExp(is) + IS_GET.marianExp(is) + IS_GET.mortgageInt(is) + IS_GET.wht(is) + IS_GET.gov(is));
      if (totalExp <= 0) return liquid > 0 ? Infinity : null;
      return liquid / totalExp;
    } },
  ],

  forecasts: [
    { key: 'grossIn',       label: 'Gross inflow',           category: 'Cash Flow',        format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (r) => r?.grossIn },
    { key: 'baseBurn',      label: 'Base burn',              category: 'Cash Flow',        format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (r) => r?.baseBurn },
    { key: 'fuel',          label: 'Fuel',                   category: 'Overlays',         format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (r) => r?.fuel || 0 },
    { key: 'seasonal',      label: 'Seasonal',               category: 'Overlays',         format: 'currency', tone: 'warn',    betterWhen: 'lower',  extract: (r) => r?.seasonal || 0 },
    { key: 'lifestyle',     label: 'Lifestyle',              category: 'Overlays',         format: 'currency', tone: 'warn',    betterWhen: 'lower',  extract: (r) => r?.lifestyle || 0 },
    { key: 'fvfMRR',        label: 'Recurring revenue',      category: 'Revenue Drivers',  format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (r) => r?.fvfMRR || 0 },
    { key: 'netFCF',        label: 'Net FCF',                category: 'Cash Flow',        format: 'currency', tone: 'neutral', betterWhen: 'higher', extract: (r) => r?.netFCF },
    { key: 'liquidEnding',  label: 'Liquid ending',          category: 'Liquidity',        format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (r) => r?.liquidEnding },
    { key: 'ccDebt',        label: 'Short-term debt',        category: 'Balance Sheet',    format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (_r, { bs }) => BS_GET.ccDebt(bs) },
    { key: 'equity',        label: 'Equity',                 category: 'Balance Sheet',    format: 'currency', tone: 'neutral', betterWhen: 'higher', extract: (_r, { bs }) => BS_GET.equity(bs) },
  ],

  synthesis: [
    { key: 'grossIn',       label: 'Gross inflow',           category: 'Actuals / Budgets / Forecasts', format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (r) => r?.grossIn },
    { key: 'baseBurn',      label: 'Base burn',              category: 'Actuals / Budgets / Forecasts', format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (r) => r?.baseBurn },
    { key: 'netFCF',        label: 'Net FCF',                category: 'Actuals / Budgets / Forecasts', format: 'currency', tone: 'neutral', betterWhen: 'higher', extract: (r) => r?.netFCF },
    { key: 'liquidEnding',  label: 'Liquid ending',          category: 'Actuals / Budgets / Forecasts', format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (r) => r?.liquidEnding },
    { key: 'fvfMRR',        label: 'Recurring revenue',      category: 'Revenue Drivers',            format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (r) => r?.fvfMRR || 0 },
    { key: 'netIncome',     label: 'Net Income',             category: 'Profitability',              format: 'currency', tone: 'neutral', betterWhen: 'higher', extract: (_r, { is }) => IS_GET.netIncome(is) },
  ],

  fcc: [
    { key: 'liquidEnding',  label: 'Liquid ending',          category: 'Liquidity',        format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (r) => r?.liquidEnding },
    { key: 'ccDebt',        label: 'Short-term debt',        category: 'Liabilities',      format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (_r, { bs }) => BS_GET.ccDebt(bs) },
    /* Short-term credits — scenario-level fallback; forecast doesn't track
       per-period. Reads live total from config so the row shows the
       current short-term credit balance held constant, not em-dash. */
    { key: 'ccCredits',     label: 'Short-term credits',     category: 'Liabilities',      format: 'currency', tone: 'good',    betterWhen: 'higher', extract: (_r, { bs }) => BS_GET.ccCredits(bs) || totalCCCredits() },
    { key: 'ltl',           label: 'Long-term liabilities',  category: 'Liabilities',      format: 'currency', tone: 'bad',     betterWhen: 'lower',  extract: (_r, { bs }) => BS_GET.ltl(bs) },
    { key: 'fixedAssets',   label: 'NC-Assets net',          category: 'Assets',           format: 'currency', tone: 'neutral', betterWhen: 'neutral',extract: (_r, { bs }) => BS_GET.fixedAssets(bs) },
    { key: 'netFCF',        label: 'Net FCF',                category: 'Cash Flow',        format: 'currency', tone: 'neutral', betterWhen: 'higher', extract: (r) => r?.netFCF },
    { key: 'netIncome',     label: 'Net Income',             category: 'Cash Flow',        format: 'currency', tone: 'neutral', betterWhen: 'higher', extract: (_r, { is }) => IS_GET.netIncome(is) },
  ],
};

export function buildKPIsTimeline(scenario, moduleId, opts = {}) {
  const registry = MODULE_KPI_REGISTRY[moduleId] || [];
  const direction = opts.direction || getTabDirection(scenario, moduleId);
  const periods = Math.max(1, opts.periods || normalizePeriodScalar(scenario.formalPeriodCount, 12));
  const density = Math.max(1, opts.density || normalizePeriodScalar(scenario.formalPeriodDensity, 1));
  const totalMonths = periods * density;

  const raw = buildThreeStatementForDirection(scenario, direction, totalMonths, density);
  const forecast         = aggregateByDensity(raw.forecast,         density, 'last');
  const incomeStatements = aggregateByDensity(raw.incomeStatements, density, 'sum');
  const balanceSheets    = aggregateByDensity(raw.balanceSheets,    density, 'last');
  const cashFlows        = aggregateByDensity(raw.cashFlows,        density, 'sum');
  const periodLabels     = forecast.map(r => fmtPeriod(r.ym ?? r.label, density));
  const currentState     = currentPeriodStateForRows(forecast, density);
  const currentLabel     = currentState.label;

  const rows = registry.map((kpi) => ({
    ratio:       kpi.label,          /* reuse MetricHorizonTable's row shape */
    category:    kpi.category,
    format:      kpi.format,
    tone:        kpi.tone,
    betterWhen:  kpi.betterWhen,     /* v8 Path AI — metric polarity (see computeTrendTone) */
    direction:   kpi.direction,      /* v8 Path AM — direction guide for tooltip */
    formula:     kpi.formula,
    hint:        kpi.hint,
    explanation: kpi.explanation,
    values:      forecast.map((row, i) => {
      try { return kpi.extract(row, { is: incomeStatements[i], bs: balanceSheets[i], cf: cashFlows[i] }); }
      catch { return null; }
    }),
  }));

  return { periodLabels, currentLabel, currentState, rows, direction, periods, density, moduleId };
}
